import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { createPortal, flushSync } from 'react-dom';
import axios from 'axios';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  LayoutGrid,
  Search,
  Eraser,
  FileText,
  ZoomIn,
  ZoomOut,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { siteOperationalUi, streamCameraOperationalUi } from '../data/inventoryCameraOperational';
import CameraDropZone from '../components/CameraDropZone';
import RecordingsSpoaModal, { type EvidencePhoto, type SpoaCameraInfo } from '../components/RecordingsSpoaModal';
import { RecordingCard } from '../components/RecordingCard';
import {
  usePlayerState,
  MAX_CAMERAS,
  TIMELINE_MS,
  RECORDING_PLAYBACK_FORWARD_MINUTES,
  RECORDING_MAX_PLAYLIST_MINUTES,
  RECORDING_PRE_ROLL_MINUTES,
  RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING,
  effectiveRecordingAnchorWallTime,
  type PlayerSlot,
  type RecordingViewSessionReport,
} from '../hooks/usePlayerState';
import XTAMSpinner from '../components/XTAMSpinner';
import { useFullscreen } from '../hooks/useFullscreen';
import toast from 'react-hot-toast';
import { useNetworkHealth } from '../hooks/useNetworkHealth';
import './RecordingsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

/**
 * El POST /playback/request devuelve `playlistUrl` en cuanto el video-engine acepta el prepare;
 * el GET a `stream.m3u8` puede tardar (FFmpeg/NFS). Sin esperar aquí, hls.js reintenta en bucle.
 * Sincronizamos: no devolvemos al reproductor hasta tener manifiesto HLS legible (200 + #EXTM3U).
 */
async function waitForRecordingManifestReachable(
  playlistUrl: string,
  options?: { maxWaitMs?: number; intervalMs?: number; fetchTimeoutMs?: number }
): Promise<void> {
  const maxWaitMs = options?.maxWaitMs ?? 45_000;
  const intervalMs = options?.intervalMs ?? 200;
  const fetchTimeoutMs = options?.fetchTimeoutMs ?? 8_000;
  const deadline = Date.now() + maxWaitMs;
  let lastStatus = 0;
  let lastKind: string | null = null;
  let lastDetail: string | null = null;
  while (Date.now() < deadline) {
    try {
      const ac = new AbortController();
      const tid = window.setTimeout(() => ac.abort(), fetchTimeoutMs);
      const r = await fetch(playlistUrl, {
        method: 'GET',
        cache: 'no-store',
        signal: ac.signal,
      }).finally(() => window.clearTimeout(tid));
      lastStatus = r.status;
      if (r.ok) {
        const text = await r.text();
        if (text.trimStart().startsWith('#EXTM3U')) {
          return;
        }
        // 200 sin #EXTM3U: caso raro, suele indicar respuesta truncada del proxy. Log explícito.
        console.warn('[waitForRecordingManifest] 200 sin #EXTM3U', { url: playlistUrl, preview: text.slice(0, 80) });
      } else {
        // Backend distingue por `kind`: engine-timeout(504), engine-unreachable(502), engine-status(502 con r.status), o 503 “no segments yet”.
        // Logueamos cada intento para que se pueda diagnosticar la causa de la espera larga.
        let body: { error?: string; kind?: string; detail?: string; status?: number } = {};
        try { body = await r.json(); } catch { /* respuesta no-JSON */ }
        lastKind = body.kind ?? null;
        lastDetail = body.error ?? body.detail ?? null;
        console.warn('[waitForRecordingManifest] intento fallido', {
          httpStatus: r.status,
          kind: body.kind ?? '(none)',
          error: body.error,
          detail: body.detail,
          engineStatus: body.status,
        });
      }
    } catch (e: any) {
      // AbortController (fetchTimeoutMs) o caída de red real.
      const aborted = e?.name === 'AbortError';
      lastKind = aborted ? 'frontend-abort' : 'network-error';
      lastDetail = e?.message ?? String(e);
      console.warn('[waitForRecordingManifest] fetch falló', { kind: lastKind, detail: lastDetail });
    }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  throw new Error(
    `El manifiesto HLS no estuvo disponible en ${Math.round(maxWaitMs / 1000)} s ` +
    `(último HTTP ${lastStatus || 'n/c'}${lastKind ? `, kind=${lastKind}` : ''}${lastDetail ? `, ${lastDetail}` : ''}). ` +
    `Posibles causas: video-engine encolado (subseg_queue_wait), NFS lento o sin segmentos en ese wallStart.`
  );
}

/**
 * Misma regla que usePlayerState.setTimelinePosition: porcentaje 0–100 en el día completo.
 * Usa medianoche de `t` (no leer timelineWindow del estado) para no desinc al cambiar hora/selector
 * con `selectedDateStr` aún no rehidratado.
 */
function dateToTimelinePercent(t: Date, timelineWindowStart: Date): number {
  const tws = timelineWindowStart.getTime();
  const tms = t.getTime();
  if (tms <= tws) return 0;
  const end = tws + TIMELINE_MS;
  if (tms >= end) return 100;
  return ((tms - tws) / TIMELINE_MS) * 100;
}

const EVIDENCE_STORAGE_PREFIX = 'vms.rx.evidence.v1:';
const MAX_EVIDENCE_PHOTOS = 6;

/** Carga evidencias del día: clave estable `fecha` o migración desde `fecha|slots…`. */
/** Misma regla que al quitar cámara: fotos con `cameraId` o legado por slot+nombre. */
function evidencePhotoBelongsToSlotCamera(
  p: EvidencePhoto,
  cameraId: string,
  cameraName: string,
  slotId: number
): boolean {
  if (p.cameraId) return p.cameraId === cameraId;
  return p.slotId === slotId && p.cameraName === cameraName;
}

/** Solo entradas con data URL de imagen válida (evita miniaturas negras por JSON truncado o datos corruptos). */
function sanitizeEvidencePhotos(raw: unknown[]): EvidencePhoto[] {
  const out: EvidencePhoto[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const p = item as Partial<EvidencePhoto>;
    if (typeof p.id !== 'string' || typeof p.dataUrl !== 'string') continue;
    const d = p.dataUrl.trim();
    if (!d.startsWith('data:image/')) continue;
    if (d.length < 80) continue;
    out.push({
      id: p.id,
      dataUrl: d,
      slotId: typeof p.slotId === 'number' ? p.slotId : 0,
      cameraId: typeof p.cameraId === 'string' ? p.cameraId : undefined,
      cameraName: typeof p.cameraName === 'string' ? p.cameraName : '',
      wallClockLabel: typeof p.wallClockLabel === 'string' ? p.wallClockLabel : '',
      videoTimeSec: typeof p.videoTimeSec === 'number' ? p.videoTimeSec : 0,
    });
  }
  return out;
}

function loadEvidencePhotosFromStorage(dateStr: string): EvidencePhoto[] {
  try {
    const direct = sessionStorage.getItem(`${EVIDENCE_STORAGE_PREFIX}${dateStr}`);
    if (direct) {
      const parsed = JSON.parse(direct) as unknown;
      return Array.isArray(parsed) ? sanitizeEvidencePhotos(parsed) : [];
    }
    const legacyPrefix = `${EVIDENCE_STORAGE_PREFIX}${dateStr}|`;
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k?.startsWith(legacyPrefix)) continue;
      const raw = sessionStorage.getItem(k);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) return sanitizeEvidencePhotos(parsed);
      } catch {
        continue;
      }
    }
  } catch {
    // ignore
  }
  return [];
}

/** Reduce tamaño para sessionStorage y decodificación fiable en el modal SPOA (capturas HD muy grandes). */
function compressEvidenceDataUrl(dataUrl: string): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) return Promise.resolve(dataUrl);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const maxW = 960;
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (w === 0 || h === 0) {
          resolve(dataUrl);
          return;
        }
        const targetW = Math.min(w, maxW);
        const targetH = Math.round((h * targetW) / w);
        const c = document.createElement('canvas');
        c.width = targetW;
        c.height = targetH;
        const ctx = c.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }
        ctx.drawImage(img, 0, 0, targetW, targetH);
        resolve(c.toDataURL('image/jpeg', 0.86));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Envía la hora con offset de zona (ej. -05:00) para que backend y video-engine reciban el instante correcto (10 p.m. local = 10 p.m., no 5 p.m.). */
function toLocalISOString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const offsetMin = -d.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const h = Math.floor(Math.abs(offsetMin) / 60);
  const m = Math.abs(offsetMin) % 60;
  const tz = `${sign}${pad(h)}:${pad(m)}`;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.000${tz}`;
}

/** Convierte error de API (string u objeto {code, message}) a string para mostrar en UI */
function toErrorString(x: unknown): string {
  if (x == null) return '';
  if (typeof x === 'string') return x;
  if (typeof x === 'object' && x !== null) {
    const o = x as { message?: string; error?: string };
    if (typeof o.message === 'string') return o.message;
    if (typeof o.error === 'string') return o.error;
  }
  return String(x);
}

/** 12 h estable en ASCII (mismo patrón que el usuario puede editar). */
function formatLocalTime12hHm(date: Date): string {
  if (!Number.isFinite(date.getTime())) return '12:00 a. m.';
  const h24 = date.getHours();
  const min = date.getMinutes();
  const isPm = h24 >= 12;
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${String(min).padStart(2, '0')} ${isPm ? 'p. m.' : 'a. m.'}`;
}

function formatLocalTime12hHms(date: Date): string {
  if (!Number.isFinite(date.getTime())) return '--:--:--';
  const h24 = date.getHours();
  const min = date.getMinutes();
  const sec = date.getSeconds();
  const isPm = h24 >= 12;
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')} ${isPm ? 'p. m.' : 'a. m.'}`;
}

/** `HH:mm:ss` para alinear con la hora de ancla/segmento (misma precisión que el reloj de la barra). */
function toTimeInputValueHms(date: Date): string {
  if (!Number.isFinite(date.getTime())) return '00:00:00';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(
    date.getSeconds()
  ).padStart(2, '0')}`;
}

/** HMS del `<input type="time">` (no acoplar el valor mostrado a `playerState.selectedTime`: el reproductor lo mueve al preparar, pausar o el timeline). */
function parseRecordingTimeInputHms(raw: string): { h: number; min: number; sec: number } | null {
  const v = raw.trim();
  if (!v) return null;
  const parts = v.split(':').map((p) => parseInt(p, 10));
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return null;
  const h = parts[0]!;
  const mm = parts[1]!;
  const sec = parts.length >= 3 ? parts[2]! : 0;
  if (h < 0 || h > 23 || mm < 0 || mm > 59 || sec < 0 || sec > 59) return null;
  return { h, min: mm, sec };
}

interface Site {
  id: string;
  name: string;
  address?: string;
  serviceName?: string;
}

interface Camera {
  id: string;
  name: string;
  description?: string;
  /** Semáforo operativo desde ``GET /cameras`` (``on`` / ``off`` / ``issue`` / ``unknown``). */
  operational?: 'on' | 'off' | 'issue' | 'unknown';
  status: string;
  rtspUrl?: string;
  siteAddress?: string;
  site?: Site;
  siteId?: string;
  cameraType?: 'pvvc' | 'privado' | 'masivo';
  isMassive?: boolean;
  deviceCamera?: {
    connectivityOnline?: boolean | null;
    lastConnectivityStatus?: boolean | null;
    probeState?: number | null;
    lastHeartbeatAt?: string | null;
  } | null;
}

type GridLayout = '1x1' | '1x2' | '2x2';
type ServiceKey = 'PVVC' | 'Privados' | 'EU' | 'Masivos';
const SERVICE_KEYS: ServiceKey[] = ['PVVC', 'Privados', 'EU', 'Masivos'];

interface SchemaSiteNode {
  id: string;
  name: string;
  address?: string;
  serviceKey: ServiceKey;
  cameras: Camera[];
}

const GRANT_RECORDINGS_DOWNLOAD = 'xtam_vms:recordings_download';
const GRANT_RECORDINGS_PREVIEW = 'xtam_vms:recordings_preview';

type RecordingPickerPrepareSource = 'day' | 'time' | 'now';

const RecordingsPage: React.FC = () => {
  const { token, user } = useAuth();
  const networkHealth = useNetworkHealth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [gridLayout, setGridLayout] = useState<GridLayout>('1x1');
  const [isSchemaSidebarCollapsed, setIsSchemaSidebarCollapsed] = useState(true);
  const [isDraggingFromSchema, setIsDraggingFromSchema] = useState(false);
  /** Fallback cuando el drop desde el portal no entrega getData (algunos navegadores) */
  const draggedCameraForDropRef = useRef<Camera | null>(null);
  const addCameraToSlotRef = useRef<
    (
      slotId: number,
      camera: NonNullable<PlayerSlot['camera']>,
      prepareAt?: Date,
      playbackDurationMinutes?: number
    ) => Promise<void>
  >(() => Promise.resolve());
  const removeCameraFromSlotRef = useRef<(slotId: number) => void>(() => {});
  const playerSlotsRef = useRef<PlayerSlot[]>([]);
  const setTimelinePositionRef = useRef<(percent: number) => void>(() => {});
  /**
   * Minutos hacia adelante desde el ancla al preparar desde analíticas/certificado (mismo criterio que drag manual).
   * Debe alinearse con `prepareStream` (forward + pre-roll y cap `RECORDING_MAX_PLAYLIST_MINUTES`);
   * valores muy bajos acortan el m3u8 y disparan rolls y nuevos `/playback/request` en pocos minutos.
   */
  const analyticsPlaybackMinutesRef = useRef(RECORDING_PLAYBACK_FORWARD_MINUTES);
  const lastSlotIdUnderCursorRef = useRef<number | null>(null);
  const [schemaSearchQuery, setSchemaSearchQuery] = useState('');
  const [expandedServices, setExpandedServices] = useState<Record<ServiceKey, boolean>>({
    PVVC: true,
    Privados: true,
    EU: true,
    Masivos: true,
  });
  const [expandedSites, setExpandedSites] = useState<Record<string, boolean>>({});
  const nowInitial = new Date();
  const [selectedDate, setSelectedDate] = useState(nowInitial);
  const [selectedDateStr, setSelectedDateStr] = useState(
    () => `${nowInitial.getFullYear()}-${String(nowInitial.getMonth() + 1).padStart(2, '0')}-${String(nowInitial.getDate()).padStart(2, '0')}`
  );
  /**
   * Calendario nativo: las flechas / cambio de mes pueden disparar `change` sin confirmar día.
   * Mantenemos borrador en el input y solo aplicamos fecha (y prepare) al cerrar el popover (`blur`).
   */
  const [dateInputDraft, setDateInputDraft] = useState<string | null>(null);
  /** Qué control disparó el prepare: loader en fecha solo día u hora; no timeline / mapa / analíticas. */
  const [recordingPickerPrepareSource, setRecordingPickerPrepareSource] =
    useState<RecordingPickerPrepareSource | null>(null);
  const wasSlotPreparingRef = useRef(false);
  const prepareSourceIdleClearTimerRef = useRef<number | null>(null);
  /** Dedupe `input`+`change` del `<input type="time">` con el mismo instante. */
  const lastTimeInputCommitKeyRef = useRef<string>('');
  /** Evita pisar el valor del input mientras el usuario escribe en el selector nativo. */
  const recordingNativeTimeFocusedRef = useRef(false);
  /**
   * Marca de tiempo del último commit no-cero del `<input type="time">`. Chrome/Edge a veces
   * emiten un `input`/`change` espurio con "00:00:00" al cerrar el popup nativo por pérdida
   * de foco (típico al iniciar un drag): si llega muy seguido tras un valor real, lo descartamos.
   */
  const lastNonZeroTimeInputCommitMsRef = useRef<number>(0);
  const [syncWithLiveTime, setSyncWithLiveTime] = useState(false);
  const [draggedCamera, setDraggedCamera] = useState<Camera | null>(null);
  const [expandedSlotId, setExpandedSlotId] = useState<number | null>(null);
  /**
   * DOM host del modal que recibe el `<video>` portaled desde CameraDropZone al maximizar.
   * El modal permanece montado (oculto con `--hidden`) para que el host sea siempre estable
   * y no se desmonte el `<video>` (preservando buffer Hls.js — m3u8/ts cache).
   */
  const [expandedPlayerHost, setExpandedPlayerHostState] = useState<HTMLDivElement | null>(null);
  const setExpandedPlayerHost = useCallback((el: HTMLDivElement | null) => {
    setExpandedPlayerHostState(el);
  }, []);

  /** Zoom temporal: 1 = día completo; cada paso divide la ventana visible por 2 (máx. 32× ≈ 45 min). */
  const [timelineZoom, setTimelineZoom] = useState(1);
  /** Inicio de la ventana visible (ms desde medianoche del día seleccionado). */
  const [timelinePanMs, setTimelinePanMs] = useState(0);
  /** Tooltip al mover el cursor sobre la línea de tiempo. */
  const [timelineHover, setTimelineHover] = useState<{ x: number; label: string } | null>(null);
  /**
   * Red LTE / datos limitados (Network Information API): ocultar 4× y forzar máx. 2×
   * para reducir buffering y bucles de fragmento en grabaciones HLS.
   */
  const [constrainedNetwork, setConstrainedNetwork] = useState(false);

  const getSlotCount = useCallback((layout: GridLayout): number => {
    switch (layout) {
      case '1x1': return 1;
      case '1x2': return 2;
      case '2x2': return 4;
      default: return 4;
    }
  }, []);

  const slotCount = Math.min(getSlotCount(gridLayout), MAX_CAMERAS);

  /** Solicita playback para una cámara (backend → Video Engine).
   *  Reintenta una vez si la primera tentativa falla por timeout de manifiesto / red,
   *  no para errores 4xx con motivo concreto (mala request, sin grabaciones, etc.). */
  const requestPlaybackForCamera = useCallback(
    async (params: { siteId: number; cameraId: number; startTime: Date; endTime: Date; anchorTime?: Date }) => {
      if (!token) throw new Error('No token');

      const attempt = async () => {
        const res = await axios.post(
          `${API_URL}/recordings/playback/request`,
          {
            siteId: params.siteId,
            cameraId: params.cameraId,
            startTime: toLocalISOString(params.startTime),
            endTime: toLocalISOString(params.endTime),
            ...(params.anchorTime != null && Number.isFinite(params.anchorTime.getTime())
              ? { anchorTime: toLocalISOString(params.anchorTime) }
              : {}),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = res.data?.data ?? res.data;
        const requestId = data?.requestId ?? res.data?.requestId;
        if (!requestId) throw new Error('No requestId');
        const playlistUrl = data?.playlistUrl ?? data?.playlist_url ?? null;
        const startOffsetSeconds = data?.startOffsetSeconds ?? data?.start_offset_seconds ?? 0;
        const hlsRaw = data?.hlsWindowStart as string | undefined;
        const hlsParsed = hlsRaw != null && hlsRaw !== '' ? new Date(hlsRaw) : null;
        const hlsStart =
          hlsParsed && Number.isFinite(hlsParsed.getTime()) ? hlsParsed : null;
        if (playlistUrl && typeof playlistUrl === 'string') {
          await waitForRecordingManifestReachable(playlistUrl);
        }
        return {
          requestId,
          playlistUrl,
          startTime: hlsStart ?? params.startTime,
          endTime: params.endTime,
          startOffsetSeconds,
        };
      };

      const isRetriable = (e: any): boolean => {
        // 4xx con motivo del servidor: no reintentar (mala request, sin grabaciones…)
        const status = e?.response?.status as number | undefined;
        if (status != null && status >= 400 && status < 500) return false;
        const msg = typeof e?.message === 'string' ? e.message : '';
        // Reintenta en timeouts/red/manifiesto inaccesible (con TS de 10 s la primera
        // subsegmentación FFmpeg puede pasarse del deadline; el segundo intento ya halla caché).
        return /manifiesto|timeout|aborted|network|ECONN|ETIMEDOUT|ENETUNREACH/i.test(msg) || status == null || status >= 500;
      };

      try {
        return await attempt();
      } catch (firstError: any) {
        if (!isRetriable(firstError)) {
          const d = firstError?.response?.data;
          const nested = d?.error;
          const msg =
            (typeof nested === 'object' && nested && 'message' in nested && typeof nested.message === 'string'
              ? nested.message
              : null) ||
            (typeof nested === 'string' ? nested : null) ||
            (typeof d?.message === 'string' ? d.message : null) ||
            (typeof firstError?.message === 'string' ? firstError.message : null);
          throw new Error(msg ?? 'Error al solicitar la grabación');
        }
        try {
          return await attempt();
        } catch (secondError: any) {
          const d = secondError?.response?.data;
          const nested = d?.error;
          const msg =
            (typeof nested === 'object' && nested && 'message' in nested && typeof nested.message === 'string'
              ? nested.message
              : null) ||
            (typeof nested === 'string' ? nested : null) ||
            (typeof d?.message === 'string' ? d.message : null) ||
            (typeof secondError?.message === 'string' ? secondError.message : null);
          throw new Error(msg ?? 'Error al solicitar la grabación (tras reintento)');
        }
      }
    },
    [token]
  );

  /** Solicita playback para varias cámaras al mismo tiempo con una única llamada HTTP batch.
   *  Reintenta el POST una vez si falla por timeout/red (no para 4xx con cuerpo de error del servidor). */
  const requestBatchPlaybackForCameras = useCallback(
    async (cameras: Array<{ slotId: number; siteId: number; cameraId: number; startTime: Date; endTime: Date; anchorTime?: Date }>) => {
      if (!token) throw new Error('No token');
      const body = {
        cameras: cameras.map((c) => ({
          siteId: c.siteId,
          cameraId: c.cameraId,
          startTime: toLocalISOString(c.startTime),
          endTime: toLocalISOString(c.endTime),
          ...(c.anchorTime != null && Number.isFinite(c.anchorTime.getTime())
            ? { anchorTime: toLocalISOString(c.anchorTime) }
            : {}),
        })),
      };
      const isRetriableBatch = (e: any): boolean => {
        const status = e?.response?.status as number | undefined;
        if (status != null && status >= 400 && status < 500) return false;
        const msg = typeof e?.message === 'string' ? e.message : '';
        return /timeout|aborted|network|ECONN|ETIMEDOUT|ENETUNREACH/i.test(msg) || status == null || status >= 500;
      };
      let res;
      try {
        res = await axios.post(`${API_URL}/recordings/playback/batch-request`, body, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (firstError: any) {
        if (!isRetriableBatch(firstError)) throw firstError;
        res = await axios.post(`${API_URL}/recordings/playback/batch-request`, body, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      const items = (res.data?.data ?? []) as Array<{
        ok: boolean;
        cameraId: number;
        siteId: number;
        requestId?: string;
        playlistUrl?: string | null;
        startOffsetSeconds?: number;
        hlsWindowStart?: string;
        error?: string;
        statusCode?: number;
      }>;

      return cameras.map((cam) => {
        const item = items.find((i) => i.cameraId === cam.cameraId && i.siteId === cam.siteId);
        if (!item || !item.ok || !item.requestId) {
          return {
            slotId: cam.slotId,
            requestId: item?.requestId ?? '',
            playlistUrl: null as string | null,
            startTime: cam.startTime,
            endTime: cam.endTime,
            startOffsetSeconds: 0,
            error: item?.error ?? 'Sin respuesta del servidor',
          };
        }
        const hlsRaw = item.hlsWindowStart;
        const hlsParsed = hlsRaw ? new Date(hlsRaw) : null;
        const hlsStart = hlsParsed && Number.isFinite(hlsParsed.getTime()) ? hlsParsed : null;
        return {
          slotId: cam.slotId,
          requestId: item.requestId,
          playlistUrl: item.playlistUrl ?? null,
          startTime: hlsStart ?? cam.startTime,
          endTime: cam.endTime,
          startOffsetSeconds: item.startOffsetSeconds ?? 0,
        };
      });
    },
    [token]
  );

  /** Poll hasta completed. Lanza Error si failed, timeout o error HTTP (para que la UI muestre el motivo real). */
  const pollUntilCompleted = useCallback(
    async (requestId: string): Promise<{
      playlistUrl: string;
      startOffsetSeconds?: number;
      hlsWindowStart?: Date;
    }> => {
      if (!token) throw new Error('No hay sesión');
      const maxAttempts = 120;
      const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
      for (let count = 0; count < maxAttempts; count++) {
        let r;
        try {
          r = await axios.get(`${API_URL}/recordings/playback/status/${requestId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (err: unknown) {
          const e = err as { response?: { status?: number; data?: { error?: { message?: string } | string; message?: string } } };
          const d = e?.response?.data;
          const nested = d?.error;
          const piece =
            (typeof nested === 'object' && nested && 'message' in nested && typeof nested.message === 'string'
              ? nested.message
              : null) ||
            (typeof nested === 'string' ? nested : null) ||
            (typeof d?.message === 'string' ? d.message : null);
          throw new Error(
            piece || `No se pudo consultar el estado de la grabación${e?.response?.status ? ` (${e.response.status})` : ''}`
          );
        }
        const d = r.data?.data ?? r.data;
        if (d?.status === 'completed') {
          const url = d?.playlistUrl ?? d?.playlist_url;
          if (url) {
            const hlsStr = d?.hlsWindowStart as string | undefined;
            const hlsParsed = hlsStr != null && hlsStr !== '' ? new Date(hlsStr) : null;
            const hlsWindowStart =
              hlsParsed && Number.isFinite(hlsParsed.getTime()) ? hlsParsed : undefined;
            return {
              playlistUrl: url,
              startOffsetSeconds: d?.startOffsetSeconds ?? d?.start_offset_seconds,
              hlsWindowStart,
            };
          }
          throw new Error('El servidor marcó la grabación como lista pero no devolvió URL de reproducción');
        }
        if (d?.status === 'failed') {
          const errMsg = d?.errorMessage ?? d?.error_message ?? 'La preparación de la grabación falló';
          throw new Error(typeof errMsg === 'string' ? errMsg : 'La preparación de la grabación falló');
        }
        await delay(1000);
      }
      throw new Error('Tiempo de espera agotado al preparar la grabación');
    },
    [token]
  );

  const reportRecordingViewSession = useCallback(async (payload: RecordingViewSessionReport) => {
    if (!token) return;
    try {
      await axios.post(`${API_URL}/recordings/audit/view-session`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      /* auditoría best-effort */
    }
  }, [token]);

  const [recordingExtendOffer, setRecordingExtendOffer] = useState<{ windowEndMs: number } | null>(null);
  const handleRecordingWindowNearEnd = useCallback((info: { windowEndMs: number }) => {
    setRecordingExtendOffer((prev) => {
      if (prev?.windowEndMs === info.windowEndMs) return prev;
      return info;
    });
  }, []);

  /**
   * Debe declararse antes de `usePlayerState`: el hook usa `getRecordingSearchWallTime` en
   * `prepareStream`/`play` para anclar al día del calendario + valor del `<input type="time">`,
   * no solo a `selectedTime` (que el prepare puede dejar en medianoche u otra hora desincronizada).
   */
  const [recordingTimeInputStr, setRecordingTimeInputStr] = useState(() => toTimeInputValueHms(nowInitial));
  const recordingTimeInputStrRef = useRef(recordingTimeInputStr);
  recordingTimeInputStrRef.current = recordingTimeInputStr;

  const getRecordingSearchWallTime = useCallback((): Date | null => {
    const ymd = selectedDateStr.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return null;
    const parsed = parseRecordingTimeInputHms(recordingTimeInputStrRef.current);
    if (!parsed) return null;
    const wall = new Date(y, m - 1, d, parsed.h, parsed.min, parsed.sec, 0);
    return Number.isFinite(wall.getTime()) ? wall : null;
  }, [selectedDateStr]);

  /** Estado global del reproductor (Play/Stop, timeline, slots con prepareStream/startPlayback) */
  const playerState = usePlayerState({
    slotCount,
    selectedDate,
    selectedDateStr,
    token,
    requestPlaybackForCamera,
    requestBatchPlaybackForCameras,
    pollUntilCompleted,
    recordingPreRollMinutes: RECORDING_PRE_ROLL_MINUTES,
    reportRecordingViewSession,
    onRecordingWindowNearEnd: RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING
      ? undefined
      : handleRecordingWindowNearEnd,
    getRecordingSearchWallTime,
  });

  const activeCameraCount = playerState.slots.slice(0, slotCount).filter(s => s.camera != null).length;

  useEffect(() => {
    if (playerState.mode === 'STOPPED') {
      setRecordingExtendOffer(null);
    }
  }, [playerState.mode]);

  const handleDismissRecordingExtendOffer = useCallback(() => {
    setRecordingExtendOffer(null);
  }, []);

  const handleConfirmRecordingExtendOffer = useCallback(() => {
    setRecordingExtendOffer(null);
    playerState.extendRecordingHlsWindowWhilePlaying();
  }, [playerState.extendRecordingHlsWindowWhilePlaying]);

  /**
   * Valor del `<input type="time">`: solo lo actualizamos desde el propio input, «Ahora», día confirmado
   * o deep link (mapa/analíticas). No desde `selectedTime` interno (timeline, HLS, pausa, sync en vivo).
   * (Estado declarado antes de `usePlayerState` para cablear `getRecordingSearchWallTime`.)
   */

  /** Solo el día: si la clave dependiera de los slots, al cerrar una cámara cambiaría y se “perderían” todas las fotos. */
  const recordingsContextKey = useMemo(() => selectedDateStr, [selectedDateStr]);

  const canRecordingsDownload = useMemo(() => {
    const a = user?.allowedActions;
    if (!Array.isArray(a)) return true;
    return a.includes(GRANT_RECORDINGS_DOWNLOAD);
  }, [user?.allowedActions]);

  const recordingMaxHoursBack = user?.recordingMaxHoursBack ?? null;

  const [evidencePhotos, setEvidencePhotos] = useState<EvidencePhoto[]>([]);
  const [spoaModalOpen, setSpoaModalOpen] = useState(false);

  const activeCamerasForSpoa: SpoaCameraInfo[] = useMemo(() => {
    const siteMap = new Map(sites.map((s) => [String(s.id), s]));
    return playerState.slots
      .filter((s) => s.camera)
      .map((s) => {
        const cam = s.camera!;
        const siteId = cam.siteId ?? cam.site?.id;
        const site = siteId ? siteMap.get(String(siteId)) : undefined;
        return {
          id: String(cam.id),
          name: cam.name,
          siteAddress: site?.address,
          siteName: site?.name ?? cam.site?.name,
        };
      });
  }, [playerState.slots, sites]);
  const skipNextEvidencePersistRef = useRef(false);

  useLayoutEffect(() => {
    skipNextEvidencePersistRef.current = true;
    setEvidencePhotos(loadEvidencePhotosFromStorage(recordingsContextKey));
  }, [recordingsContextKey]);

  useEffect(() => {
    if (skipNextEvidencePersistRef.current) {
      skipNextEvidencePersistRef.current = false;
      return;
    }
    try {
      sessionStorage.setItem(`${EVIDENCE_STORAGE_PREFIX}${recordingsContextKey}`, JSON.stringify(evidencePhotos));
    } catch {
      // límite sessionStorage / modo privado
    }
  }, [evidencePhotos, recordingsContextKey]);

  const handleEvidenceCapture = useCallback(
    (info: {
      slotId: number;
      cameraId: string;
      dataUrl: string;
      cameraName: string;
      videoTimeSec: number;
      wallClockMs: number;
    }) => {
      const raw = (info.dataUrl ?? '').trim();
      if (!raw.startsWith('data:image/')) {
        toast.error('Captura inválida (no es una imagen). Prueba de nuevo.', { duration: 2800 });
        return;
      }
      const wall = new Date(info.wallClockMs);
      const wallClockLabel = formatLocalTime12hHms(wall);
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `ev-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      void compressEvidenceDataUrl(raw).then((dataUrl) => {
        setEvidencePhotos((prev) => {
          const countForCamera = prev.filter((p) =>
            evidencePhotoBelongsToSlotCamera(p, info.cameraId, info.cameraName, info.slotId)
          ).length;
          if (countForCamera >= MAX_EVIDENCE_PHOTOS) {
            queueMicrotask(() => {
              toast.error(`Máximo ${MAX_EVIDENCE_PHOTOS} fotos por cámara.`, {
                duration: 3000,
                id: `ev-max-${info.cameraId || info.cameraName}`,
              });
            });
            return prev;
          }
          queueMicrotask(() => {
            toast.success(`Foto guardada · ${info.cameraName}`, {
              duration: 2200,
              id: `ev-ok-${id}`,
            });
          });
          return [
            ...prev,
            {
              id,
              dataUrl,
              slotId: info.slotId,
              cameraId: info.cameraId,
              cameraName: info.cameraName,
              wallClockLabel,
              videoTimeSec: info.videoTimeSec,
              wallClockMs: info.wallClockMs,
            },
          ];
        });
      });
    },
    []
  );

  const removeEvidencePhoto = useCallback((photoId: string) => {
    setEvidencePhotos((prev) => prev.filter((p) => p.id !== photoId));
  }, []);

  /**
   * El calendario actualiza selectedDateStr en el padre; usePlayerState arma timelineWindowStart desde ese string.
   * Si llamamos setSelectedTime(nuevo día + hora) en el mismo handler que setSelectedDateStr, el clamp sigue
   * usando el día anterior → hay que elegir dos veces. Diferimos setSelectedTime a useLayoutEffect cuando
   * selectedDateStr ya coincide con el valor elegido.
   */
  const datePickerCommitStrRef = useRef<string | null>(null);
  const datePickerPreserveHmsRef = useRef({ h: 0, min: 0, sec: 0 });
  const selectedTimeForPickerRef = useRef(playerState.selectedTime);
  selectedTimeForPickerRef.current = playerState.selectedTime;

  const visibleTimelineMs = TIMELINE_MS / timelineZoom;
  const maxTimelinePanMs = Math.max(0, TIMELINE_MS - visibleTimelineMs);

  useEffect(() => {
    setTimelinePanMs((p) => Math.min(Math.max(0, p), maxTimelinePanMs));
  }, [timelineZoom, maxTimelinePanMs]);

  /** Al cambiar el día en el calendario, volver a vista del día completo. */
  useEffect(() => {
    setTimelinePanMs(0);
    setTimelineZoom(1);
  }, [selectedDateStr]);

  useEffect(() => {
    const nav = navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        saveData?: boolean;
        addEventListener?: (type: string, fn: () => void) => void;
        removeEventListener?: (type: string, fn: () => void) => void;
      };
    };
    const c = nav.connection;
    if (!c) return;
    const evalConstrained = () => {
      const eff = c.effectiveType ?? '';
      const dl = c.downlink;
      const constrained =
        c.saveData === true ||
        eff === 'slow-2g' ||
        eff === '2g' ||
        eff === '3g' ||
        (typeof dl === 'number' && dl > 0 && dl < 3);
      setConstrainedNetwork(constrained);
    };
    evalConstrained();
    c.addEventListener?.('change', evalConstrained);
    return () => c.removeEventListener?.('change', evalConstrained);
  }, []);

  // Keep a stable ref to setSelectedTime so the live-sync effect can always
  // call the latest version without adding playerState to its dep array.
  // Adding playerState itself would cause an infinite re-render cascade:
  // setSelectedTime → re-render → new playerState object → effect re-runs → setSelectedTime…
  const setSelectedTimeRef = useRef(playerState.setSelectedTime);
  setSelectedTimeRef.current = playerState.setSelectedTime;
  const playerModeRef = useRef(playerState.mode);
  /** Hay HLS de grabación listo: el puntero no debe “seguir el reloj” (desalinearía el prepare / play). */
  const hasReadyRecordingPlaybackRef = useRef(false);
  playerModeRef.current = playerState.mode;
  hasReadyRecordingPlaybackRef.current = playerState.slots.some(
    (s) => s.streamStatus === 'READY' && s.playback != null
  );
  addCameraToSlotRef.current = playerState.addCameraToSlot;
  removeCameraFromSlotRef.current = playerState.removeCameraFromSlot;
  playerSlotsRef.current = playerState.slots;
  setTimelinePositionRef.current = playerState.setTimelinePosition;

  /** Quitar cámara del slot y eliminar solo las evidencias de esa cámara (no todo el listado). */
  const removeCameraFromSlotAndEvidence = useCallback((slotId: number) => {
    const slot = playerSlotsRef.current.find((s) => s.id === slotId);
    const cam = slot?.camera;
    const removedId = cam ? String(cam.id) : '';
    const removedName = cam?.name ?? '';
    removeCameraFromSlotRef.current(slotId);
    if (!cam) return;
    setEvidencePhotos((prev) =>
      prev.filter((p) => {
        if (p.cameraId) return p.cameraId !== removedId;
        return !(p.slotId === slotId && p.cameraName === removedName);
      })
    );
  }, []);

  const toastedPlaybackRequestIds = useRef<Set<string>>(new Set());

  /** Al completar un prepare, informar ancla (puntero) e inicio de pista .ts según el servidor. */
  useEffect(() => {
    for (const s of playerState.slots) {
      if (s.streamStatus !== 'READY' || !s.playback?.requestId) continue;
      const id = s.playback.requestId;
      if (toastedPlaybackRequestIds.current.has(id)) continue;
      toastedPlaybackRequestIds.current.add(id);
      const pb = s.playback;
      const anchor = pb.timelineAnchorWallTime;
      const t0 = pb.startTime;
      if (!Number.isFinite(t0.getTime())) {
        toast.success('Grabación preparada (según el servidor / XTAM).', { duration: 4000 });
        continue;
      }
      if (anchor == null || !Number.isFinite(anchor.getTime())) {
        toast.success(
          `Inicio de pista (XTAM / servidor): ${formatLocalTime12hHms(t0)}`,
          { duration: 4000 }
        );
        continue;
      }
      const showBoth = Math.abs(anchor.getTime() - t0.getTime()) > 2000;
      if (showBoth) {
        toast.success(
          `Hora de referencia (puntero): ${formatLocalTime12hHms(anchor)} · Pista (primer .ts): ${formatLocalTime12hHms(
            t0
          )}`,
          { duration: 5200 }
        );
      } else {
        toast.success(`Hora alineada (XTAM / servidor): ${formatLocalTime12hHms(anchor)}`, { duration: 4000 });
      }
    }
  }, [playerState.slots]);

  const fromMapAppliedRef = useRef(false);
  const fromMapSiteIdRef = useRef<number | null>(null);
  const fromAnalyticsAppliedRef = useRef(false);
  const fromAnalyticsCameraIdRef = useRef<number | null>(null);
  /** siteId en query (?siteId=) al abrir desde analíticas: refuerza site en /playback y desambigua cámara. */
  const fromAnalyticsSiteIdRef = useRef<number | null>(null);
  /** Hora exacta para el primer prepareStream (evita race: selectedTime del estado aún no actualizado). */
  const analyticsPrepareAnchorRef = useRef<Date | null>(null);
  /** Hora del evento analítico: se aplica en un efecto posterior a cambiar selectedDate (ver comentario abajo). */
  const pendingAnalyticsAnchorRef = useRef<Date | null>(null);
  const analyticsUrlAppliedRef = useRef(false);

  /** Deep link desde analíticas o certificado de descarga (mismos params: time, cameraId, siteId, playbackMin). */
  useEffect(() => {
    const fromCert = searchParams.get('cert') === '1';
    if (searchParams.get('analytics') !== '1' && !fromCert) {
      analyticsUrlAppliedRef.current = false;
      return;
    }
    const anchorTime = searchParams.get('time');
    const cameraIdStr = searchParams.get('cameraId');
    const siteIdStr = searchParams.get('siteId');
    if (!anchorTime || cameraIdStr == null || analyticsUrlAppliedRef.current) return;
    const cameraId = Number.parseInt(cameraIdStr, 10);
    if (!Number.isFinite(cameraId)) return;

    const t = new Date(anchorTime);
    if (Number.isNaN(t.getTime())) return;

    const siteIdParsed = siteIdStr != null ? Number.parseInt(siteIdStr, 10) : NaN;
    fromAnalyticsSiteIdRef.current = Number.isFinite(siteIdParsed) ? siteIdParsed : null;

    const playbackMinParam = searchParams.get('playbackMin');
    const playbackMinParsed =
      playbackMinParam != null ? Number.parseInt(playbackMinParam, 10) : NaN;
    analyticsPlaybackMinutesRef.current =
      Number.isFinite(playbackMinParsed) && playbackMinParsed >= 1 && playbackMinParsed <= RECORDING_MAX_PLAYLIST_MINUTES
        ? playbackMinParsed
        : RECORDING_PLAYBACK_FORWARD_MINUTES;

    analyticsUrlAppliedRef.current = true;
    setGridLayout('1x1');
    setSelectedDate(t);
    setSelectedDateStr(
      `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
    );
    pendingAnalyticsAnchorRef.current = t;
    analyticsPrepareAnchorRef.current = t;
    fromAnalyticsCameraIdRef.current = cameraId;
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const state = location.state as {
      siteId?: number;
      fromMap?: boolean;
      fromAnalytics?: boolean;
      cameraId?: number;
      anchorTime?: string;
    } | undefined;

    if (!state?.fromAnalytics) {
      fromAnalyticsAppliedRef.current = false;
    }

    if (state?.fromAnalytics && state.anchorTime && !fromAnalyticsAppliedRef.current) {
      fromAnalyticsAppliedRef.current = true;
      analyticsPlaybackMinutesRef.current = RECORDING_PLAYBACK_FORWARD_MINUTES;
      const t = new Date(state.anchorTime);
      if (!Number.isNaN(t.getTime())) {
        setGridLayout('1x1');
        setSelectedDate(t);
        setSelectedDateStr(
          `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
        );
        // NO llamar setSelectedTime aquí: usePlayerState.setSelectedTime hace clamp al día de selectedDate,
        // y en este render timelineWindowStart sigue siendo el día ANTERIOR → 21/03 11:17 se recorta a 00:00 del 24/03.
        pendingAnalyticsAnchorRef.current = t;
        analyticsPrepareAnchorRef.current = t;
      }
      fromAnalyticsCameraIdRef.current = state.cameraId ?? null;
      fromAnalyticsSiteIdRef.current =
        state.siteId != null && Number.isFinite(Number(state.siteId)) ? Number(state.siteId) : null;
      window.history.replaceState({}, document.title, location.pathname);
      return;
    }

    if (!state?.fromMap || state.siteId == null || fromMapAppliedRef.current) return;
    fromMapAppliedRef.current = true;
    fromMapSiteIdRef.current = state.siteId;
    const initialTime = new Date(Date.now() - 10 * 60 * 1000);
    setGridLayout('2x2');
    setSelectedDate(initialTime);
    setSelectedDateStr(
      `${initialTime.getFullYear()}-${String(initialTime.getMonth() + 1).padStart(2, '0')}-${String(initialTime.getDate()).padStart(2, '0')}`
    );
    setRecordingTimeInputStr(toTimeInputValueHms(initialTime));
    {
      const tws = new Date(
        initialTime.getFullYear(),
        initialTime.getMonth(),
        initialTime.getDate(),
        0,
        0,
        0,
        0
      );
      setTimelinePositionRef.current(dateToTimelinePercent(initialTime, tws));
    }
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.state, location.pathname]);

  // Aplicar la hora solo cuando selectedDateStr ya refleje el día del evento (si no, setSelectedTime
  // sigue haciendo clamp respecto al día anterior en el mismo tick que el efecto de location.state).
  useEffect(() => {
    const anchor = pendingAnalyticsAnchorRef.current;
    if (anchor == null) return;
    const expectedStr = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}-${String(anchor.getDate()).padStart(2, '0')}`;
    if (selectedDateStr !== expectedStr) return;
    pendingAnalyticsAnchorRef.current = null;
    setRecordingTimeInputStr(toTimeInputValueHms(anchor));
    {
      const tws = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), 0, 0, 0, 0);
      setTimelinePositionRef.current(dateToTimelinePercent(anchor, tws));
    }
    // Solo `selectedDateStr`: si dependemos de `selectedDate` y “sync con ahora” actualiza
    // `selectedDate` cada 1s (nuevo objeto), este efecto re-ejecuta sin aportar y puede encadenar con re-preps.
  }, [selectedDateStr]);

  // Al entrar desde el mapa: colocar las cámaras del sitio en los slots 1–4 cuando ya estén cargadas
  useEffect(() => {
    const siteId = fromMapSiteIdRef.current;
    if (siteId == null || loading || cameras.length === 0) return;
    const siteIdStr = String(siteId);
    const siteCameras = cameras.filter(
      (c) => String(c.siteId ?? c.site?.id ?? '') === siteIdStr
    );
    if (siteCameras.length === 0) {
      fromMapSiteIdRef.current = null;
      return;
    }
    const toAdd = siteCameras.slice(0, 4);
    fromMapSiteIdRef.current = null;
    void Promise.all(
      toAdd.map((cam, i) => addCameraToSlotRef.current(i + 1, cam))
    ).catch(() => {});
  }, [loading, cameras]);

  useEffect(() => {
    const camId = fromAnalyticsCameraIdRef.current;
    if (camId == null || loading || cameras.length === 0) return undefined;
    const siteHint = fromAnalyticsSiteIdRef.current;
    let cam = cameras.find(
      (c) =>
        String(c.id) === String(camId) &&
        (siteHint == null || Number(c.siteId ?? c.site?.id) === siteHint)
    );
    if (!cam) {
      cam = cameras.find((c) => String(c.id) === String(camId));
    }
    if (!cam) {
      fromAnalyticsCameraIdRef.current = null;
      fromAnalyticsSiteIdRef.current = null;
      analyticsPrepareAnchorRef.current = null;
      return undefined;
    }
    const siteIdResolved =
      siteHint ?? Number(cam.siteId ?? cam.site?.id ?? NaN);
    const camForSlot =
      Number.isFinite(siteIdResolved) && siteIdResolved > 0
        ? {
            ...cam,
            siteId: String(siteIdResolved),
            site: cam.site ?? {
              id: String(siteIdResolved),
              name: (cam.site as { name?: string } | undefined)?.name ?? '',
            },
          }
        : cam;

    const prepareAt = analyticsPrepareAnchorRef.current;
    analyticsPrepareAnchorRef.current = null;
    fromAnalyticsCameraIdRef.current = null;
    fromAnalyticsSiteIdRef.current = null;

    const playbackWindowMin = analyticsPlaybackMinutesRef.current;
    const t = window.setTimeout(() => {
      void addCameraToSlotRef
        .current(1, camForSlot, prepareAt ?? undefined, playbackWindowMin)
        .catch(() => {});
    }, 0);
    return () => clearTimeout(t);
  }, [loading, cameras]);

  useEffect(() => {
    const onDocDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const slotEl = el?.closest?.('[data-slot-id]');
      const id = slotEl ? Number((slotEl as HTMLElement).getAttribute('data-slot-id')) : NaN;
      lastSlotIdUnderCursorRef.current = Number.isFinite(id) ? id : null;
    };
    const onDocDrop = (e: DragEvent) => {
      const camera = draggedCameraForDropRef.current;
      if (!camera?.id) return;
      e.preventDefault();
      e.stopPropagation();
      const slotId = lastSlotIdUnderCursorRef.current ?? Number.NaN;
      if (Number.isFinite(slotId)) {
        void addCameraToSlotRef.current(slotId, camera).catch(() => {});
      }
      lastSlotIdUnderCursorRef.current = null;
      draggedCameraForDropRef.current = null;
      document.body.removeAttribute('data-dragging-from-schema');
    };
    document.addEventListener('dragover', onDocDragOver, true);
    document.addEventListener('drop', onDocDrop, true);
    return () => {
      document.removeEventListener('dragover', onDocDragOver, true);
      document.removeEventListener('drop', onDocDrop, true);
    };
  }, []);

  // No-op: el `<video>` ya no se desmonta al maximizar/restaurar, así que no hay offset que reaplicar.
  const clearPostExpandResumeForSlot = useCallback((_slotId: number) => {}, []);

  const expandOverlayRef = useRef<HTMLDivElement>(null);

  const closeExpanded = useCallback(() => {
    setExpandedSlotId(null);
  }, []);

  const fullscreen = useFullscreen(closeExpanded);

  const closeExpandedAndExitFullscreen = useCallback(() => {
    fullscreen.exit();
    setExpandedSlotId(null);
  }, [fullscreen.exit]);

  /** Cierra el modal de video ampliado al abrir/cerrar SPOA (evita dos overlays superpuestos). */
  const openSpoaModal = useCallback(() => {
    closeExpandedAndExitFullscreen();
    setSpoaModalOpen(true);
  }, [closeExpandedAndExitFullscreen]);

  const dismissSpoaModal = useCallback(() => {
    setSpoaModalOpen(false);
    closeExpandedAndExitFullscreen();
  }, [closeExpandedAndExitFullscreen]);

  const handleExpand = useCallback(
    (slotId: number | null) => {
      if (slotId == null) {
        closeExpandedAndExitFullscreen();
        return;
      }
      setExpandedSlotId(slotId);
    },
    [closeExpandedAndExitFullscreen]
  );

  const prevExpandedSlotIdRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevExpandedSlotIdRef.current;
    prevExpandedSlotIdRef.current = expandedSlotId;
    if (expandedSlotId != null && prev == null && fullscreen.isSupported && expandOverlayRef.current) {
      fullscreen.enter(expandOverlayRef.current);
    }
  }, [expandedSlotId, fullscreen.isSupported, fullscreen.enter]);

  useEffect(() => {
    loadCameras();
    loadSites();
  }, [token]);

  useEffect(() => {
    if (isSchemaSidebarCollapsed) return;
    if (!token) return;
    loadSites();
    loadCameras(true);
    const pollId = window.setInterval(() => loadCameras(true), 60_000);
    return () => window.clearInterval(pollId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSchemaSidebarCollapsed, token]);

  useEffect(() => {
    if (!syncWithLiveTime) return;
    const updateTime = () => {
      if (hasReadyRecordingPlaybackRef.current) {
        return;
      }
      const now = new Date();
      setSelectedTimeRef.current(now);
      setSelectedDate(now);
      const y = now.getFullYear();
      const mo = now.getMonth() + 1;
      const d = now.getDate();
      setSelectedDateStr(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
    // playerState intentionally NOT in deps — using ref above to avoid infinite cascade
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncWithLiveTime]);

  const loadCameras = async (force = false) => {
    if (!force && cameras.length > 0) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/cameras`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const camerasFromDB = (response.data || []).map((cam: any, index: number) => ({
        ...cam,
        id: String(cam.id ?? cam.cameraId ?? index),
        siteId: cam.site_id ?? cam.siteId,
        // Solo datos del API: no sintetizar "Sin sitio" para site_id inválido (evita nodos fantasma).
        site: cam.site ?? undefined,
        isMassive: !!(cam.isMassive ?? cam.is_massive),
        cameraType: (cam.isMassive || cam.is_massive
          ? 'masivo'
          : cam.cameraType ?? (index % 2 === 0 ? 'pvvc' : 'privado')) as 'pvvc' | 'privado' | 'masivo',
      }));
      setCameras(camerasFromDB);
    } catch (error) {
      console.error('Error cargando cámaras:', error);
      setCameras([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API_URL}/sites`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { active: true },
      });
      setSites(response.data?.data ?? response.data ?? []);
    } catch (error) {
      console.error('Error cargando sitios:', error);
    }
  };

  const camerasInUse = useMemo(
    () => new Set(playerState.slots.map((s) => s.camera?.id).filter(Boolean)),
    [playerState.slots]
  );

  const getServiceKey = useCallback((camera: Camera, siteFromIndex?: Site): ServiceKey => {
    const name = (camera.site as Site | undefined)?.serviceName ?? siteFromIndex?.serviceName ?? '';
    const n = String(name).toUpperCase();
    if (n.includes('MASIV') || camera.isMassive || camera.cameraType === 'masivo') return 'Masivos';
    if (n === 'PVVC') return 'PVVC';
    if (n === 'EU') return 'EU';
    if (n === 'PRIVADO' || n === 'PRIVADOS') return 'Privados';
    if (camera.cameraType === 'pvvc') return 'PVVC';
    return 'Privados';
  }, []);

  /** Cámaras cuyo sitio está en la lista actual de sitios (evita nodos "Sin sitio" por datos obsoletos). */
  const camerasForSchema = useMemo(() => {
    const siteIds = new Set(sites.map((s) => String(s.id)));
    return cameras.filter((cam) => {
      const sid = cam.siteId ?? cam.site?.id;
      if (sid == null || sid === '') return false;
      return siteIds.has(String(sid));
    });
  }, [cameras, sites]);

  const schemaTree = useMemo(() => {
    const siteIndex = new Map<string, Site>();
    sites.forEach((s) => siteIndex.set(String(s.id), s));
    const nodes = new Map<string, SchemaSiteNode>();
    camerasForSchema.forEach((cam) => {
      const sid = String(cam.siteId ?? cam.site?.id ?? '');
      const siteFromIndex = siteIndex.get(sid);
      if (!siteFromIndex) return;
      const serviceKey = getServiceKey(cam, siteFromIndex);
      const name = cam.site?.name ?? siteFromIndex.name;
      const address = cam.site?.address ?? siteFromIndex.address;
      const existing = nodes.get(sid);
      if (existing) {
        existing.cameras.push(cam);
        return;
      }
      nodes.set(sid, { id: sid, name, address, serviceKey, cameras: [cam] });
    });
    const buckets: Record<ServiceKey, { sites: SchemaSiteNode[] }> = {
      PVVC: { sites: [] },
      Privados: { sites: [] },
      EU: { sites: [] },
      Masivos: { sites: [] },
    };
    nodes.forEach((node) => buckets[node.serviceKey].sites.push(node));
    (Object.keys(buckets) as ServiceKey[]).forEach((k) => {
      buckets[k].sites.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      buckets[k].sites.forEach((s) => s.cameras.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })));
    });
    return buckets;
  }, [camerasForSchema, sites, getServiceKey]);

  const filteredSchemaTree = useMemo(() => {
    const q = schemaSearchQuery.trim().toLowerCase();
    if (!q) return schemaTree;
    const out: Record<ServiceKey, { sites: SchemaSiteNode[] }> = {
      PVVC: { sites: [] },
      Privados: { sites: [] },
      EU: { sites: [] },
      Masivos: { sites: [] },
    };
    (Object.keys(schemaTree) as ServiceKey[]).forEach((key) => {
      schemaTree[key].sites.forEach((site) => {
        const siteMatch = site.name.toLowerCase().includes(q) || (site.address ?? '').toLowerCase().includes(q);
        const camMatch = site.cameras.some(
          (c) => c.name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q)
        );
        if (siteMatch || camMatch) out[key].sites.push(site);
      });
    });
    return out;
  }, [schemaTree, schemaSearchQuery]);

  useEffect(() => {
    const q = schemaSearchQuery.trim();
    if (!q) return;
    const nextSvc: Partial<Record<ServiceKey, boolean>> = {};
    const nextSites: Record<string, boolean> = {};
    SERVICE_KEYS.forEach((k) => {
      if (filteredSchemaTree[k].sites.length > 0) {
        nextSvc[k] = true;
        filteredSchemaTree[k].sites.forEach((s) => { nextSites[s.id] = true; });
      }
    });
    if (Object.keys(nextSvc).length > 0) setExpandedServices((p) => ({ ...p, ...nextSvc }));
    if (Object.keys(nextSites).length > 0) setExpandedSites((p) => ({ ...p, ...nextSites }));
  }, [schemaSearchQuery, filteredSchemaTree]);

  const toggleService = (key: ServiceKey) => {
    setExpandedServices((p) => ({ ...p, [key]: !(p[key] ?? true) }));
  };
  const toggleSite = (siteId: string) => {
    setExpandedSites((p) => ({ ...p, [siteId]: !(p[siteId] ?? false) }));
  };

  const SITE_DROP_TYPE = 'application/x-vms-site';

  /** El árbol del panel puede agrupar por `cam-${id}` si la API no trae site; al arrastrar, heredamos el sitio padre si es numérico. */
  const mergeCameraWithSchemaSite = (camera: Camera, schemaSite: SchemaSiteNode): Camera => {
    const fromCam = camera.siteId ?? camera.site?.id;
    const hasRealSiteFromCam =
      fromCam != null && String(fromCam).trim() !== '' && !String(fromCam).startsWith('cam-');
    const schemaId = String(schemaSite.id);
    const numericParentSite = /^\d+$/.test(schemaId);
    const resolvedSiteKey = hasRealSiteFromCam ? String(fromCam) : numericParentSite ? schemaId : undefined;
    return {
      ...camera,
      ...(resolvedSiteKey != null ? { siteId: resolvedSiteKey } : {}),
      site: {
        id: resolvedSiteKey ?? camera.site?.id ?? schemaId,
        name: camera.site?.name ?? schemaSite.name,
        address: camera.site?.address ?? schemaSite.address,
        ...(camera.site?.serviceName != null ? { serviceName: camera.site.serviceName } : {}),
      },
    };
  };

  const handleDragStart = (e: React.DragEvent, camera: Camera, fromSchema = false) => {
    setDraggedCamera(camera);
    if (fromSchema) {
      setIsDraggingFromSchema(true);
      draggedCameraForDropRef.current = camera;
      document.body.setAttribute('data-dragging-from-schema', '1');
      e.dataTransfer.effectAllowed = 'copy';
    } else {
      e.dataTransfer.effectAllowed = 'move';
    }
    const cameraJson = JSON.stringify(camera);
    e.dataTransfer.setData('application/json', cameraJson);
    e.dataTransfer.setData('text/plain', fromSchema ? `vms-camera:${cameraJson}` : camera.id);
  };

  const handleSiteDragStart = (site: SchemaSiteNode, siteCameras: Camera[], e: React.DragEvent) => {
    setDraggedCamera(null);
    setIsDraggingFromSchema(true);
    document.body.setAttribute('data-dragging-from-schema', '1');
    const cameraIds = siteCameras.map((c) => String(c.id));
    const payload = JSON.stringify({ siteId: site.id, siteName: site.name, cameraIds });
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(SITE_DROP_TYPE, payload);
    e.dataTransfer.setData('text/plain', `vms-site:${payload}`);
  };

  const handleDragEnd = () => {
    setDraggedCamera(null);
    setIsDraggingFromSchema(false);
    // Retrasar limpieza para que el drop pueda leer el ref (en algunos navegadores dragend llega antes que drop)
    setTimeout(() => {
      draggedCameraForDropRef.current = null;
      document.body.removeAttribute('data-dragging-from-schema');
    }, 0);
  };

  const handleDropSite = useCallback(
    async (slotId: number, cameraIds: string[]) => {
      const camerasToAdd = cameraIds
        .map((id) => cameras.find((c) => String(c.id) === String(id)))
        .filter((c): c is Camera => c != null);
      if (camerasToAdd.length === 0) return;
      // Slots visibles según la grilla (1×1 … 2×2): reemplazar contenido, no solo huecos vacíos
      const visibleSlots = playerSlotsRef.current.slice(0, slotCount);
      const slotIds = visibleSlots.map((s) => s.id);
      if (slotIds.length === 0) return;
      const startIdx = slotIds.includes(slotId) ? slotIds.indexOf(slotId) : 0;
      const count = Math.min(camerasToAdd.length, slotIds.length);
      await Promise.all(
        Array.from({ length: count }, (_, i) => {
          const targetSlotId = slotIds[(startIdx + i) % slotIds.length]!;
          return addCameraToSlotRef.current(targetSlotId, camerasToAdd[i]!);
        })
      );
    },
    [cameras, slotCount]
  );

  /** Añadir cámara desde el panel sin cola global: un prepare lento ya no bloquea el resto de drops. */
  const addCameraFromSchemaDrop = useCallback((slotId: number, camera: NonNullable<PlayerSlot['camera']>) => {
    void addCameraToSlotRef.current(slotId, camera).catch(() => {});
  }, []);

  /** Drop a nivel de slot para que funcione aunque el drop caiga en RecordingCard u otra zona del slot */
  const handleSlotDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleSlotDrop = useCallback(
    (slotId: number, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const cameraFromRef = draggedCameraForDropRef.current;
      if (cameraFromRef?.id != null && cameraFromRef?.name != null) {
        addCameraFromSchemaDrop(slotId, cameraFromRef);
        return;
      }
      const SITE_TYPE = 'application/x-vms-site';
      let siteData = e.dataTransfer.getData(SITE_TYPE);
      if (!siteData) {
        const text = e.dataTransfer.getData('text/plain');
        if (typeof text === 'string' && text.startsWith('vms-site:')) siteData = text.slice(9);
      }
      if (siteData && handleDropSite) {
        try {
          const data = JSON.parse(siteData) as { cameraIds: string[] };
          if (Array.isArray(data.cameraIds) && data.cameraIds.length > 0) {
            handleDropSite(slotId, data.cameraIds);
            return;
          }
        } catch {
          /* fall through */
        }
      }
      let camJson = e.dataTransfer.getData('application/json');
      if (!camJson) {
        const text = e.dataTransfer.getData('text/plain');
        if (typeof text === 'string' && text.startsWith('vms-camera:')) camJson = text.slice(11);
      }
      if (camJson) {
        try {
          const parsed = JSON.parse(camJson) as { id: string; name: string };
          if (parsed?.id && parsed?.name) {
            const fullCamera = cameras.find((c) => String(c.id) === String(parsed.id));
            if (fullCamera) addCameraFromSchemaDrop(slotId, fullCamera);
          }
        } catch {
          /* ignore */
        }
      }
    },
    [cameras, addCameraFromSchemaDrop, handleDropSite]
  );

  /** Pasos de salto en la línea de tiempo (segundos): ±15 s estilo DVR/NVR. */
  const PLAYBACK_REWIND_SECONDS = 15;
  const PLAYBACK_FORWARD_SECONDS = 15;

  const hasSeekableRecording = useMemo(
    () =>
      playerState.slots.some((s) => s.camera && s.streamStatus === 'READY' && s.playback),
    [playerState.slots]
  );

  /**
   * Botón Play/Pause: si está reproduciendo, pausa (conserva la posición); si está
   * pausado o detenido, reproduce. Desde PAUSED continúa desde el frame actual; desde
   * STOPPED arranca desde la fecha solicitada (lógica en `usePlayerState.play`).
   */
  const handlePlayPause = useCallback(() => {
    if (playerState.mode === 'PLAYING') playerState.pause();
    else void playerState.play();
  }, [playerState.mode, playerState.pause, playerState.play]);

  /** Botón Stop: detiene, lleva el vídeo a 0 y deja el puntero en la fecha solicitada. */
  const handleStop = useCallback(() => {
    setSyncWithLiveTime(false);
    playerState.stop();
  }, [playerState.stop]);

  const seekPlaybackBySeconds = useCallback(
    (deltaSec: number) => {
      setSyncWithLiveTime(false);
      const r = playerState.seekBySecondsLocal(deltaSec);
      if (!r.ok) {
        if (r.reason === 'no_ready_slots') {
          toast.error(
            'No hay grabación lista: espere a que el vídeo esté listo (estado READY) para retroceder o adelantar.',
            { duration: 4500 }
          );
        } else {
          toast.error(
            'Las ventanas HLS de las cámaras no se solapan: use la misma franja temporal o menos cámaras.',
            { duration: 5500 }
          );
        }
      }
    },
    [playerState.seekBySecondsLocal]
  );

  const handlePrevious = useCallback(
    () => seekPlaybackBySeconds(-PLAYBACK_REWIND_SECONDS),
    [seekPlaybackBySeconds]
  );
  const handleNext = useCallback(
    () => seekPlaybackBySeconds(PLAYBACK_FORWARD_SECONDS),
    [seekPlaybackBySeconds]
  );

  /**
   * Parar + puntero al inicio de la ventana HLS ya cargada: seek local (sin nuevo prepare).
   */
  const handleRestartFromStart = useCallback(() => {
    setSyncWithLiveTime(false);
    playerState.stopAndResetToRecordingStart();
  }, [playerState.stopAndResetToRecordingStart]);

  const playbackSpeedOptions = constrainedNetwork ? ([0.5, 1, 2] as const) : ([0.5, 1, 2, 4] as const);

  useEffect(() => {
    if (constrainedNetwork && playerState.playbackSpeed > 2) {
      playerState.setPlaybackSpeed(2);
    }
  }, [constrainedNetwork, playerState.playbackSpeed, playerState.setPlaybackSpeed]);

  /** Convierte posición X en el rail a porcentaje del día completo (respeta zoom/pan). */
  const applyTimelineClientX = useCallback(
    (clientX: number, barEl: HTMLElement) => {
      setSyncWithLiveTime(false);
      const rect = barEl.getBoundingClientRect();
      const fraction = rect.width > 0 ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) : 0;
      const absoluteMs = timelinePanMs + fraction * visibleTimelineMs;
      if (recordingMaxHoursBack != null) {
        const now = new Date();
        const twsMs = playerState.timelineWindowStart.getTime();
        const cutoffMs = now.getTime() - recordingMaxHoursBack * 3600_000 - twsMs;
        if (absoluteMs < cutoffMs) {
          toast.error(
            `Restricción de perfil: solo puede acceder a grabaciones de las últimas ${recordingMaxHoursBack} hora${recordingMaxHoursBack !== 1 ? 's' : ''}.`,
            { duration: 3500, id: 'recording-max-hours-block' }
          );
          return;
        }
      }
      const percent = (absoluteMs / TIMELINE_MS) * 100;
      playerState.setTimelinePosition(percent);
    },
    [timelinePanMs, visibleTimelineMs, playerState.setTimelinePosition, recordingMaxHoursBack, playerState.timelineWindowStart]
  );

  const handleTimelinePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    applyTimelineClientX(e.clientX, el);
    const onMove = (ev: PointerEvent) => {
      applyTimelineClientX(ev.clientX, el);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  };

  const handleTimelineMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const fraction = rect.width > 0 ? Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) : 0;
    const absoluteMs = timelinePanMs + fraction * visibleTimelineMs;
    const hoverDate = new Date(playerState.timelineWindowStart.getTime() + absoluteMs);
    setTimelineHover({
      x: e.clientX - rect.left,
      label: Number.isFinite(hoverDate.getTime()) ? formatLocalTime12hHms(hoverDate) : '--:--:--',
    });
  };

  const handleTimelineMouseLeave = () => setTimelineHover(null);

  const handleTimelineWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const fraction = rect.width > 0 ? Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) : 0.5;
    const T = timelinePanMs + fraction * visibleTimelineMs;
    const zoomIn = e.deltaY < 0;
    const nextZoom = zoomIn ? Math.min(64, timelineZoom * 2) : Math.max(1, timelineZoom / 2);
    if (nextZoom === timelineZoom) return;
    const nextVisible = TIMELINE_MS / nextZoom;
    const newPan = T - fraction * nextVisible;
    const maxP = Math.max(0, TIMELINE_MS - nextVisible);
    setTimelineZoom(nextZoom);
    setTimelinePanMs(Math.min(Math.max(0, newPan), maxP));
  };

  const handleTimelineZoomIn = () => {
    if (timelineZoom >= 64) return;
    const next = timelineZoom * 2;
    const nextVisible = TIMELINE_MS / next;
    const selMs = playerState.selectedTime.getTime() - playerState.timelineWindowStart.getTime();
    const centered = selMs - nextVisible / 2;
    const maxP = Math.max(0, TIMELINE_MS - nextVisible);
    setTimelineZoom(next);
    setTimelinePanMs(Math.min(Math.max(0, centered), maxP));
  };

  const handleTimelineZoomOut = () => {
    if (timelineZoom <= 1) return;
    const next = timelineZoom / 2;
    const nextVisible = TIMELINE_MS / next;
    const selMs = playerState.selectedTime.getTime() - playerState.timelineWindowStart.getTime();
    const centered = selMs - nextVisible / 2;
    const maxP = Math.max(0, TIMELINE_MS - nextVisible);
    setTimelineZoom(next);
    setTimelinePanMs(Math.min(Math.max(0, centered), maxP));
  };

  const handleTimelinePanLeft = () => {
    const step = visibleTimelineMs * 0.25;
    setTimelinePanMs((p) => Math.max(0, p - step));
  };

  const handleTimelinePanRight = () => {
    const step = visibleTimelineMs * 0.25;
    setTimelinePanMs((p) => Math.min(maxTimelinePanMs, p + step));
  };

  const schedulePrepareSourceIdleFallback = () => {
    if (prepareSourceIdleClearTimerRef.current != null) {
      clearTimeout(prepareSourceIdleClearTimerRef.current);
      prepareSourceIdleClearTimerRef.current = null;
    }
    prepareSourceIdleClearTimerRef.current = window.setTimeout(() => {
      prepareSourceIdleClearTimerRef.current = null;
      const preparing = playerSlotsRef.current.some((s) => s.camera && s.streamStatus === 'PREPARING');
      if (!preparing) {
        setRecordingPickerPrepareSource(null);
      }
    }, 650) as unknown as number;
  };

  /** Solo actualiza el día en estado React; la hora en el timeline se aplica en useLayoutEffect (ver datePickerCommitStrRef). */
  const applyDateValue = (value: string) => {
    const trimmed = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return;
    const [y, m, d] = trimmed.split('-').map(Number);
    if (!y || !m || !d) return;
    const nextDate = new Date(y, m - 1, d);
    if (Number.isNaN(nextDate.getTime())) return;
    if (trimmed === selectedDateStr) return;
    const fromInput = parseRecordingTimeInputHms(recordingTimeInputStrRef.current);
    if (fromInput) {
      datePickerPreserveHmsRef.current = fromInput;
    } else {
      const src = Number.isFinite(selectedTimeForPickerRef.current.getTime())
        ? selectedTimeForPickerRef.current
        : new Date();
      datePickerPreserveHmsRef.current = {
        h: src.getHours(),
        min: src.getMinutes(),
        sec: src.getSeconds(),
      };
    }
    datePickerCommitStrRef.current = trimmed;
    setRecordingPickerPrepareSource('day');
    schedulePrepareSourceIdleFallback();
    setSelectedDate(nextDate);
    setSelectedDateStr(trimmed);
    setSyncWithLiveTime(false);
  };

  const dateInputDraftRef = useRef<string | null>(null);

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    dateInputDraftRef.current = v;
    setDateInputDraft(v);
  };

  const handleDateInputBlur = () => {
    const draft = dateInputDraftRef.current;
    dateInputDraftRef.current = null;
    setDateInputDraft(null);
    if (draft != null) applyDateValue(draft.trim());
  };

  useLayoutEffect(() => {
    const commit = datePickerCommitStrRef.current;
    if (commit == null || commit !== selectedDateStr) return;
    datePickerCommitStrRef.current = null;
    const [y, m, d] = commit.split('-').map(Number);
    if (!y || !m || !d) return;
    /**
     * Releer la hora actual del input — el usuario puede haberla cambiado entre `applyDateValue`
     * (que congeló el HMS en `datePickerPreserveHmsRef`) y este efecto. Sin esto, al combinar
     * cambio de fecha + cambio de hora el efecto pedía la grabación con la hora vieja y se
     * traía un video de otra fecha/hora.
     */
    const preserved = datePickerPreserveHmsRef.current;
    const parsedLatest = parseRecordingTimeInputHms(recordingTimeInputStrRef.current);
    /**
     * `applyDateValue` ya guardó la HMS a conservar en `datePickerPreserveHmsRef`. Si entre tanto el
     * ref del `<input type="time">` quedó en "00:00:00" (rebote al cerrar el popup o carrera de renders),
     * `parsedLatest` sigue siendo un objeto válido y `??` no cae al preserve → el cambio de día forzaba
     * medianoche. Solo ignoramos medianoche parseada cuando el preserve no es medianoche.
     */
    let h = preserved.h;
    let min = preserved.min;
    let sec = preserved.sec;
    if (parsedLatest != null) {
      const parsedMidnight =
        parsedLatest.h === 0 && parsedLatest.min === 0 && parsedLatest.sec === 0;
      const preservedNonZero = preserved.h !== 0 || preserved.min !== 0 || preserved.sec !== 0;
      if (!parsedMidnight || !preservedNonZero) {
        h = parsedLatest.h;
        min = parsedLatest.min;
        sec = parsedLatest.sec;
      }
    }
    const nextTime = new Date(y, m - 1, d, h, min, sec, 0);
    if (Number.isNaN(nextTime.getTime())) return;
    setRecordingTimeInputStr(toTimeInputValueHms(nextTime));
    /** Mismo criterio que el cambio de hora: `setTimelinePosition` puede no disparar prepare si el % cae en el mismo ms que el puntero o la ventana aún no alinea el día. */
    playerState.forceRecordingSlotsPrepareAtTime(nextTime);
  }, [selectedDateStr, playerState.forceRecordingSlotsPrepareAtTime]);

  const commitRecordingTimeFromInput = (raw: string) => {
    setSyncWithLiveTime(false);
    const v = raw.trim();
    if (!v) return;
    const parts = v.split(':').map((p) => parseInt(p, 10));
    if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return;
    const hh = parts[0]!;
    const mm = parts[1]!;
    const sec = parts.length >= 3 ? parts[2]! : 0;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59 || sec < 0 || sec > 59) return;
    /**
     * Defensa: el `<input type="time" step="1">` de Chrome/Edge dispara `input`/`change` con
     * "00:00:00" al cerrar el popup por focus loss (típico al iniciar un drag de cámara).
     * Si llega un cero a ras de un commit no-cero (≤ 800 ms), lo tratamos como rebote del popup
     * y lo ignoramos: si no, sobreescribiríamos el ancla y `prepareStream` pediría medianoche.
     */
    const isAllZero = hh === 0 && mm === 0 && sec === 0;
    const nowMs = Date.now();
    if (isAllZero && nowMs - lastNonZeroTimeInputCommitMsRef.current < 800) {
      return;
    }
    /**
     * Día siempre el del calendario confirmado (`selectedDateStr`), no el de `playerState.selectedTime`.
     * Si el usuario acaba de cambiar la fecha y aún no se ha propagado el estado (race entre
     * `applyDateValue` y este handler), preferir `datePickerCommitStrRef.current` (la fecha pendiente)
     * — sin esto, escribir la hora justo tras elegir un día nuevo pedía la grabación del día anterior.
     */
    const pendingDayStr =
      datePickerCommitStrRef.current && /^\d{4}-\d{2}-\d{2}$/.test(datePickerCommitStrRef.current)
        ? datePickerCommitStrRef.current
        : null;
    const ymd = (pendingDayStr ?? selectedDateStr).trim();
    let nextTime: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [y, m, d] = ymd.split('-').map(Number);
      if (!y || !m || !d) return;
      nextTime = new Date(y, m - 1, d, hh, mm, sec, 0);
    } else {
      const base = Number.isFinite(playerState.selectedTime.getTime())
        ? playerState.selectedTime
        : Number.isFinite(selectedDate.getTime())
          ? selectedDate
          : new Date();
      nextTime = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm, sec, 0);
    }
    if (!Number.isFinite(nextTime.getTime())) return;
    if (nextTime.getTime() === playerState.selectedTime.getTime()) return;
    if (recordingMaxHoursBack != null) {
      const cutoff = new Date(Date.now() - recordingMaxHoursBack * 3600_000);
      if (nextTime.getTime() < cutoff.getTime()) {
        toast.error(
          `Restricción de perfil: solo puede acceder a grabaciones de las últimas ${recordingMaxHoursBack} hora${recordingMaxHoursBack !== 1 ? 's' : ''}.`,
          { duration: 3500, id: 'recording-max-hours-block' }
        );
        return;
      }
    }
    const commitKey = String(nextTime.getTime());
    if (lastTimeInputCommitKeyRef.current === commitKey) return;
    lastTimeInputCommitKeyRef.current = commitKey;
    if (!isAllZero) {
      lastNonZeroTimeInputCommitMsRef.current = nowMs;
    }
    datePickerPreserveHmsRef.current = { h: hh, min: mm, sec };
    setRecordingTimeInputStr(
      `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    );
    setRecordingPickerPrepareSource('time');
    schedulePrepareSourceIdleFallback();
    setSelectedDate(nextTime);
    playerState.forceRecordingSlotsPrepareAtTime(nextTime);
  };

  const handleRecordingNativeTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    commitRecordingTimeFromInput(e.target.value);
  };

  const handleRecordingNativeTimeInput = (e: React.FormEvent<HTMLInputElement>) => {
    // Edge/Chrome: el popup de hora actualiza el valor en `input`; a veces `change` llega tarde y el controlado no se refresca.
    commitRecordingTimeFromInput(e.currentTarget.value);
  };

  const handleRecordingNativeTimeBlur = () => {
    lastTimeInputCommitKeyRef.current = '';
  };

  const handleUseCurrentTime = () => {
    setRecordingPickerPrepareSource('now');
    schedulePrepareSourceIdleFallback();
    dateInputDraftRef.current = null;
    setDateInputDraft(null);
    const now = new Date();
    const str = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    flushSync(() => {
      setSelectedDate(now);
      setSelectedDateStr(str);
    });
    setRecordingTimeInputStr(toTimeInputValueHms(now));
    setSyncWithLiveTime(true);
    playerState.forceRecordingSlotsPrepareAtTime(now);
  };

  /** Hora mostrada: en PLAY = inicio + transcurrido; en pausa/parado = posición en timeline (incluye instante tras pausar) */
  const displayTime =
    playerState.mode === 'PLAYING' && playerState.playbackStartTimeForDisplay
      ? new Date(playerState.playbackStartTimeForDisplay.getTime() + playerState.masterElapsedSeconds * 1000)
      : playerState.selectedTime;

  /** Bloqueo mientras el backend prepara HLS (cualquier origen). */
  const isAnySlotPreparing = useMemo(
    () => playerState.slots.some((s) => s.camera && s.streamStatus === 'PREPARING'),
    [playerState.slots]
  );
  useEffect(() => {
    const preparing = isAnySlotPreparing;
    if (wasSlotPreparingRef.current && !preparing) {
      setRecordingPickerPrepareSource(null);
    }
    wasSlotPreparingRef.current = preparing;
  }, [isAnySlotPreparing]);

  useEffect(
    () => () => {
      if (prepareSourceIdleClearTimerRef.current != null) {
        clearTimeout(prepareSourceIdleClearTimerRef.current);
        prepareSourceIdleClearTimerRef.current = null;
      }
    },
    []
  );

  const src = recordingPickerPrepareSource;
  /** Loader en fecha: solo si el usuario confirmó día o cambió hora (no mes/año sueltos ni otros flujos). */
  const datePickerPrepareLoader =
    isAnySlotPreparing && (src === 'day' || src === 'time' || src === 'now');
  /** Loader en hora: cambio de hora o «Ahora»; no si solo cambió el día desde el calendario. */
  const timePickerPrepareLoader = isAnySlotPreparing && (src === 'time' || src === 'now');
  const pickerInputsBusy = isAnySlotPreparing;

  /**
   * El input de hora no sigue cada micro-ajuste del ancla HLS, pero sí debe reflejar búsquedas claras
   * (timeline, día distinto, etc.): si `selectedTime` y el string del input difieren ≥ 1 min en el mismo día
   * calendario, alineamos el input (en STOPPED y sin foco en el control).
   */
  useEffect(() => {
    if (playerState.mode === 'PLAYING') return;
    if (syncWithLiveTime) return;
    if (recordingNativeTimeFocusedRef.current) return;
    if (playerState.slots.some((s) => s.camera && s.streamStatus === 'PREPARING')) return;

    const nextStr = toTimeInputValueHms(playerState.selectedTime);
    const ymd = selectedDateStr.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      if (nextStr !== recordingTimeInputStrRef.current) {
        setRecordingTimeInputStr(nextStr);
      }
      return;
    }
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return;
    const st = playerState.selectedTime;
    const sameDay = st.getFullYear() === y && st.getMonth() + 1 === m && st.getDate() === d;
    const wallMs = (h: number, mi: number, s: number) => new Date(y, m - 1, d, h, mi, s, 0).getTime();
    const selMs = wallMs(st.getHours(), st.getMinutes(), st.getSeconds());
    const parsed = parseRecordingTimeInputHms(recordingTimeInputStrRef.current);
    const inputMs = parsed ? wallMs(parsed.h, parsed.min, parsed.sec) : null;
    const MIN_DELTA_MS = 60_000;

    if (!sameDay) {
      if (nextStr !== recordingTimeInputStrRef.current) {
        setRecordingTimeInputStr(nextStr);
      }
      return;
    }
    /**
     * Salvaguarda: si `selectedTime` quedó exactamente en medianoche del día seleccionado pero
     * el input tenía un valor claramente no-cero, casi siempre viene de un clamp espurio
     * (rebote del popup nativo de hora, `setTimelinePosition(0)`, reset por re-prepare fallido…).
     * No pisamos lo que escogió el usuario: dejamos el input como está.
     */
    const selIsMidnight = st.getHours() === 0 && st.getMinutes() === 0 && st.getSeconds() === 0;
    const inputIsNonZero =
      parsed != null && (parsed.h !== 0 || parsed.min !== 0 || parsed.sec !== 0);
    if (selIsMidnight && inputIsNonZero) {
      return;
    }
    if (inputMs == null || Math.abs(selMs - inputMs) >= MIN_DELTA_MS) {
      if (nextStr !== recordingTimeInputStrRef.current) {
        setRecordingTimeInputStr(nextStr);
      }
    }
  }, [playerState.selectedTime, playerState.mode, playerState.slots, selectedDateStr, syncWithLiveTime]);

  const isValidDate = (d: Date): boolean => Number.isFinite(d.getTime());

  const formatTime = (date: Date): string => {
    if (!isValidDate(date)) return '--:--:--';
    return formatLocalTime12hHms(date);
  };

  /** Fecha para input type="date" (YYYY-MM-DD) en hora local; evita día equivocado con toISOString en zonas como Colombia */
  const dateInputValue = (date: Date): string => {
    if (!isValidDate(date)) {
      const n = new Date();
      return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
    }
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  /** Día mostrado en el calendario = `selectedDateStr` (independiente del ancla interno hasta que se confirme día). */
  const calendarDayForDateInput = useMemo(() => {
    const str = selectedDateStr.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      return playerState.selectedTime;
    }
    const [y, m, d] = str.split('-').map(Number);
    if (!y || !m || !d) return playerState.selectedTime;
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }, [selectedDateStr, playerState.selectedTime]);

  const dateInputDisplayValue = dateInputDraft ?? dateInputValue(calendarDayForDateInput);

  /** Marcas horarias en la franja visible (adapta densidad al zoom). */
  const timelineWindowStartMs = playerState.timelineWindowStart.getTime();

  const timelineTickLabels = useMemo(() => {
    const divisions = timelineZoom >= 8 ? 16 : timelineZoom >= 4 ? 12 : 8;
    return Array.from({ length: divisions + 1 }, (_, i) => {
      const ms = timelinePanMs + (i / divisions) * visibleTimelineMs;
      const t = new Date(timelineWindowStartMs + ms);
      const label = Number.isFinite(t.getTime())
        ? divisions >= 12
          ? formatLocalTime12hHms(t)
          : formatLocalTime12hHm(t)
        : '';
      return { key: i, leftPct: (i / divisions) * 100, label };
    });
  }, [timelineZoom, timelinePanMs, visibleTimelineMs, timelineWindowStartMs]);

  const timelineIndicatorLeftPct = useMemo(() => {
    const selMs = playerState.selectedTime.getTime() - timelineWindowStartMs;
    return Math.min(100, Math.max(0, ((selMs - timelinePanMs) / visibleTimelineMs) * 100));
  }, [playerState.selectedTime, timelineWindowStartMs, timelinePanMs, visibleTimelineMs]);

  const timelineRestriction = useMemo(() => {
    if (recordingMaxHoursBack == null) return null;
    const now = new Date();
    const cutoff = new Date(now.getTime() - recordingMaxHoursBack * 3600_000);
    const cutoffMs = cutoff.getTime() - timelineWindowStartMs;
    if (cutoffMs <= 0) return null;
    if (cutoffMs >= TIMELINE_MS) return { widthPct: 100 };
    const cutoffInViewMs = cutoffMs - timelinePanMs;
    if (cutoffInViewMs <= 0) return null;
    const widthPct = Math.min(100, (cutoffInViewMs / visibleTimelineMs) * 100);
    return { widthPct };
  }, [recordingMaxHoursBack, timelineWindowStartMs, timelinePanMs, visibleTimelineMs]);

  const visibleTimelineRangeLabel = useMemo(() => {
    const m = Math.round(visibleTimelineMs / 60000);
    if (m >= 60) {
      const h = m / 60;
      return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
    }
    return `${m} min`;
  }, [visibleTimelineMs]);

  /** Controles de reproducción + línea de tiempo (vista normal y modal ampliado). */
  const renderPlaybackSection = () => (
    <>
      {playerState.globalError && (
        <div className="playback-error-banner">
          <span>{String(playerState.globalError)}</span>
          <button type="button" onClick={() => playerState.setGlobalError(null)} title="Cerrar">
            ×
          </button>
        </div>
      )}
      {recordingExtendOffer != null && (
        <div className="recording-extend-banner" role="status" aria-live="polite">
          <span>
            Queda poco de la ventana de grabación cargada (~{RECORDING_PLAYBACK_FORWARD_MINUTES} min adelante). ¿Quieres
            cargar los siguientes ~{RECORDING_PLAYBACK_FORWARD_MINUTES} minutos desde el punto actual?
          </span>
          <div className="recording-extend-banner__actions">
            <button
              type="button"
              className="btn-extend-confirm"
              onClick={handleConfirmRecordingExtendOffer}
              disabled={pickerInputsBusy}
              aria-busy={pickerInputsBusy}
            >
              Cargar
            </button>
            <button type="button" className="btn-extend-dismiss" onClick={handleDismissRecordingExtendOffer}>
              Ahora no
            </button>
          </div>
        </div>
      )}
      <div className="playback-controls">
        <div className="controls-left">
          <button
            type="button"
            className="control-btn"
            onClick={handlePrevious}
            disabled={!hasSeekableRecording}
            title="Retroceder 15 segundos (← o J)"
            aria-label="Retroceder 15 segundos en la grabación"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
          </button>
          <button
            type="button"
            className="control-btn play-btn"
            onClick={handlePlayPause}
            disabled={!hasSeekableRecording}
            title={
              playerState.mode === 'PLAYING'
                ? 'Pausar (conserva la posición actual) — Espacio o K'
                : playerState.mode === 'PAUSED'
                  ? 'Reanudar desde la posición pausada — Espacio o K'
                  : 'Reproducir desde la fecha solicitada — Espacio o K'
            }
          >
            {playerState.mode === 'PLAYING' ? (
              <svg viewBox="0 0 24 24" fill="currentColor" aria-label="Pausar">
                <rect x="6" y="5" width="4" height="14" rx="1"/>
                <rect x="14" y="5" width="4" height="14" rx="1"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" aria-label="Reproducir">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          <button
            type="button"
            className="control-btn"
            onClick={handleStop}
            disabled={!hasSeekableRecording || playerState.mode === 'STOPPED'}
            title="Detener (vuelve a la fecha solicitada)"
            aria-label="Detener la reproducción de la grabación"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-label="Detener">
              <rect x="6" y="6" width="12" height="12" rx="1"/>
            </svg>
          </button>
          <button
            type="button"
            className="control-btn"
            onClick={handleNext}
            disabled={!hasSeekableRecording}
            title="Adelantar 15 segundos (→ o L)"
            aria-label="Adelantar 15 segundos en la grabación"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
            </svg>
          </button>

          <div className="time-display">
            <svg className="clock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <span>{formatTime(displayTime)}</span>
          </div>
        </div>

        <div className="controls-center">
          <div className="speed-controls" title={constrainedNetwork ? 'Red limitada: velocidad máxima 2×' : undefined}>
            {playbackSpeedOptions.map((speed) => (
              <button
                key={speed}
                type="button"
                className={`speed-btn ${playerState.playbackSpeed === speed ? 'active' : ''}`}
                onClick={() => {
                  playerState.bumpHlsStabilityGuard();
                  playerState.setPlaybackSpeed(speed);
                }}
                title={
                  speed === 4
                    ? '4× — puede aumentar el buffering en redes lentas'
                    : `Velocidad ${speed}×`
                }
              >
                {speed}x
              </button>
            ))}
          </div>
        </div>

        <div className="controls-right">
          <div className={`date-picker ${datePickerPrepareLoader ? 'date-picker--loading' : ''}`}>
            <svg className="calendar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <input
              type="date"
              value={dateInputDisplayValue}
              onPointerDownCapture={() => setSyncWithLiveTime(false)}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={() => setSyncWithLiveTime(false)}
              onChange={handleDateInputChange}
              onBlur={handleDateInputBlur}
              title="Fecha de inicio: navegar mes o año no carga; al elegir un día y cerrar el calendario se aplica."
              disabled={pickerInputsBusy}
              aria-busy={pickerInputsBusy}
            />
            {datePickerPrepareLoader && (
              <Loader2
                className="date-picker-loader"
                size={14}
                aria-label="Cargando"
                aria-hidden="false"
              />
            )}
          </div>
          <div className={`time-picker ${timePickerPrepareLoader ? 'time-picker--loading' : ''}`}>
            <svg className="clock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <input
              type="time"
              step={1}
              className="time-picker-native"
              value={recordingTimeInputStr}
              onPointerDownCapture={() => setSyncWithLiveTime(false)}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={() => {
                recordingNativeTimeFocusedRef.current = true;
                setSyncWithLiveTime(false);
              }}
              onChange={handleRecordingNativeTimeChange}
              onInput={handleRecordingNativeTimeInput}
              onBlur={() => {
                recordingNativeTimeFocusedRef.current = false;
                handleRecordingNativeTimeBlur();
              }}
              title="Hora de inicio: confirma en el selector; también se alinea al mover el timeline (mismo día) o al cambiar de día. No sigue cada ajuste fino del segmento HLS durante la reproducción."
              aria-label="Hora de inicio de la grabación"
              disabled={pickerInputsBusy}
              aria-busy={pickerInputsBusy}
            />
            {timePickerPrepareLoader && (
              <Loader2
                className="date-picker-loader"
                size={14}
                aria-label="Cargando"
                aria-hidden="false"
              />
            )}
          </div>
          <button
            type="button"
            className="btn-use-now"
            onClick={handleUseCurrentTime}
            title="Usar fecha y hora actual"
            disabled={pickerInputsBusy}
          >
            Ahora
          </button>
        </div>
      </div>

      <div className="timeline-interactive">
        <div className="timeline-toolbar" aria-label="Escala temporal">
          <span className="timeline-range-label" title="Ventana visible">
            {visibleTimelineRangeLabel}
          </span>
          <button
            type="button"
            className="timeline-tool-btn"
            onClick={handleTimelineZoomOut}
            disabled={timelineZoom <= 1}
            title="Alejar (ver más horas)"
            aria-label="Alejar línea de tiempo"
          >
            <ZoomOut size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="timeline-tool-btn"
            onClick={handleTimelineZoomIn}
            disabled={timelineZoom >= 64}
            title="Acercar (más precisión)"
            aria-label="Acercar línea de tiempo"
          >
            <ZoomIn size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="timeline-tool-btn"
            onClick={handleTimelinePanLeft}
            disabled={timelinePanMs <= 0}
            title="Desplazar a la izquierda"
            aria-label="Ver franja anterior"
          >
            <ChevronLeft size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="timeline-tool-btn"
            onClick={handleTimelinePanRight}
            disabled={timelinePanMs >= maxTimelinePanMs}
            title="Desplazar a la derecha"
            aria-label="Ver franja siguiente"
          >
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>
        <div
          className="timeline-bar"
          onPointerDown={handleTimelinePointerDown}
          onMouseMove={handleTimelineMouseMove}
          onMouseLeave={handleTimelineMouseLeave}
          onWheel={handleTimelineWheel}
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(playerState.timelinePosition)}
          aria-label="Línea de tiempo: arrastre o clic para elegir hora; también fecha y hora arriba. Rueda del ratón para zoom."
        >
          {timelineRestriction && (
            <div
              className="timeline-restricted-zone"
              style={{ width: `${timelineRestriction.widthPct}%` }}
              title={`Zona restringida: solo puede acceder a las últimas ${recordingMaxHoursBack} hora${recordingMaxHoursBack !== 1 ? 's' : ''}`}
            />
          )}
          {timelineHover && (
            <div className="timeline-hover-tooltip" style={{ left: timelineHover.x }}>
              {timelineHover.label}
            </div>
          )}
          <div className="timeline-indicator" style={{ left: `${timelineIndicatorLeftPct}%` }} />
          <div className="timeline-hours timeline-hours--zoomed">
            {timelineTickLabels.map((tick) => (
              <div
                key={tick.key}
                className="hour-mark hour-mark--tick"
                style={{ left: `${tick.leftPct}%` }}
              >
                <span>{tick.label}</span>
              </div>
            ))}
          </div>
          <div className="timeline-segments" />
        </div>
      </div>
    </>
  );

  const schemaSidebarPortal = createPortal(
    <>
      {!isSchemaSidebarCollapsed && (
        <div
          className={`recordings-schema-backdrop ${isDraggingFromSchema ? 'recordings-schema-backdrop-drag-passthrough' : ''}`}
          aria-hidden="true"
        />
      )}
      <aside
        className={`recordings-schema-sidebar ${isSchemaSidebarCollapsed ? 'collapsed' : 'open'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="recordings-schema-header">
          <div className="recordings-schema-title">
            <LayoutGrid size={20} className="recordings-schema-title-icon" aria-hidden />
            <div className="recordings-schema-title-text">Cámaras disponibles</div>
          </div>
          <div className="recordings-schema-search-wrap">
            <Search size={16} className="recordings-schema-search-icon" aria-hidden />
            <input
              type="text"
              className="recordings-schema-search"
              placeholder="Buscar cámara..."
              value={schemaSearchQuery}
              onChange={(e) => setSchemaSearchQuery(e.target.value)}
              aria-label="Buscar cámara"
            />
          </div>
        </div>
        <div className="recordings-schema-body">
          {SERVICE_KEYS.map((svcKey) => {
            const svc = filteredSchemaTree[svcKey];
            const isOpen = expandedServices[svcKey] ?? true;
            const q = schemaSearchQuery.trim().toLowerCase();
            const availableCount = svc.sites.reduce((acc, site) => {
              let cams = site.cameras;
              if (q) cams = cams.filter((c) => c.name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q));
              return acc + cams.filter((c) => !camerasInUse.has(c.id)).length;
            }, 0);
            return (
              <div key={svcKey} className={`recordings-schema-section recordings-schema-section-${svcKey.toLowerCase()}`}>
                <button type="button" className="recordings-schema-section-header" onClick={() => toggleService(svcKey)}>
                  <span className="recordings-schema-section-dot" aria-hidden />
                  <ChevronDown size={16} className={`recordings-schema-chevron ${isOpen ? 'open' : ''}`} />
                  <span className="recordings-schema-section-name">{svcKey}</span>
                  <span className="recordings-schema-section-count">{availableCount}</span>
                </button>
                {isOpen && (
                  <div className="recordings-schema-section-content">
                    {svc.sites.map((site) => {
                      const siteOpen = expandedSites[site.id] ?? false;
                      const allSiteCams = site.cameras;
                      let cams = allSiteCams;
                      if (q) cams = cams.filter((c) => c.name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q));
                      const available = cams.filter((c) => !camerasInUse.has(c.id));
                      const siteOpUi = siteOperationalUi(allSiteCams);
                      return (
                        <div key={site.id} className="recordings-schema-site">
                          <button
                            type="button"
                            className={`recordings-schema-site-header recordings-schema-site-header--${siteOpUi.mod} ${available.length > 0 ? 'recordings-schema-site-header-draggable' : ''}`}
                            onClick={() => toggleSite(site.id)}
                            draggable={available.length > 0}
                            onDragStart={available.length > 0 ? (ev) => handleSiteDragStart(site, available, ev) : undefined}
                            onDragEnd={handleDragEnd}
                            title={
                              available.length > 0
                                ? `${siteOpUi.title} — Arrastra el sitio a la grilla para cargar todas sus cámaras`
                                : siteOpUi.title
                            }
                          >
                            <GripVertical size={14} className="recordings-schema-grip" aria-hidden />
                            <span className="recordings-schema-site-name">{site.name}</span>
                            <span className="recordings-schema-site-cam-count">
                              {siteOpUi.total === 0 ? (
                                '—'
                              ) : (
                                <>
                                  <span style={{ color: siteOpUi.countColor }}>{siteOpUi.online}</span>
                                  <span className="recordings-schema-site-cam-total">/{siteOpUi.total}</span>
                                </>
                              )}
                            </span>
                            <ChevronDown size={14} className={`recordings-schema-chevron ${siteOpen ? 'open' : ''}`} />
                          </button>
                          {siteOpen && (
                            <div className="recordings-schema-cameras">
                              {available.length === 0 ? (
                                <div className="recordings-schema-empty">
                                  {cams.length === 0 ? 'Sin cámaras cargadas' : 'Todas en vista'}
                                </div>
                              ) : (
                                available.map((cam) => {
                                  const opUi = streamCameraOperationalUi(cam);
                                  return (
                                  <div
                                    key={cam.id}
                                    className={`recordings-schema-camera recordings-schema-camera--${opUi.mod}`}
                                    draggable
                                    onDragStart={(ev) => handleDragStart(ev, mergeCameraWithSchemaSite(cam, site), true)}
                                    onDragEnd={handleDragEnd}
                                    title={`${opUi.title} — Arrastra a la grilla para agregar`}
                                  >
                                    <span
                                      className={`recordings-schema-camera-status recordings-schema-camera-status--${opUi.mod}`}
                                      aria-label={opUi.label}
                                      title={opUi.label}
                                    />
                                    <GripVertical size={14} className="recordings-schema-grip" aria-hidden />
                                    <div className="recordings-schema-camera-info">
                                      <span className="recordings-schema-camera-name">{cam.name}</span>
                                      <span className="recordings-schema-camera-address">
                                        {cam.siteAddress || cam.site?.address || site.address || cam.description || 'Sin ubicación'}
                                      </span>
                                    </div>
                                    {opUi.mod === 'off' && (
                                      <AlertTriangle size={14} className="recordings-schema-camera-warn" aria-hidden />
                                    )}
                                    <span className="recordings-schema-camera-badge">{svcKey}</span>
                                  </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {schemaSearchQuery.trim() && SERVICE_KEYS.every((k) => filteredSchemaTree[k].sites.length === 0) && (
            <div className="recordings-schema-hint recordings-schema-no-results">
              No hay resultados para &quot;{schemaSearchQuery.trim()}&quot;
            </div>
          )}
        </div>
      </aside>
      <button
        type="button"
        className={`recordings-schema-toggle ${isSchemaSidebarCollapsed ? 'collapsed' : 'open'}`}
        onClick={() => setIsSchemaSidebarCollapsed((v) => !v)}
        aria-label={isSchemaSidebarCollapsed ? 'Expandir cámaras' : 'Colapsar cámaras'}
        title={isSchemaSidebarCollapsed ? 'Expandir cámaras' : 'Colapsar cámaras'}
      >
        {isSchemaSidebarCollapsed ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>
    </>,
    document.body
  );

  if (loading) {
    return (
      <div className="recordings-page loading-state">
        <XTAMSpinner variant="inline" message="Cargando grabaciones…" />
      </div>
    );
  }

  return (
    <div className="recordings-page">
      <div
        className={`recordings-shell ${!isSchemaSidebarCollapsed ? 'schema-sidebar-open' : ''}`}
      >
        {schemaSidebarPortal}
        {/* Header: Vista + controles de grilla (incl. limpiar como icono) */}
        <div className="recordings-header">
        <div className="header-left-vista">
          <div className="view-label">Vista:</div>
          <div className="grid-controls">
            <button
              className={`grid-btn ${gridLayout === '1x1' ? 'active' : ''}`}
              onClick={() => setGridLayout('1x1')}
              title="1x1"
            >
              <svg width="22" height="22" viewBox="0 0 36 36" fill="none">
                <rect x="5" y="5" width="26" height="26" rx="2" fill="currentColor" opacity="0.8"/>
              </svg>
            </button>
            <button
              className={`grid-btn ${gridLayout === '1x2' ? 'active' : ''}`}
              onClick={() => setGridLayout('1x2')}
              title="1x2"
            >
              <svg width="22" height="22" viewBox="0 0 36 36" fill="none">
                <rect x="3" y="5" width="14" height="26" rx="2" fill="currentColor" opacity="0.75"/>
                <rect x="19" y="5" width="14" height="26" rx="2" fill="currentColor" opacity="0.75"/>
              </svg>
            </button>
            <button
              className={`grid-btn ${gridLayout === '2x2' ? 'active' : ''}`}
              onClick={() => setGridLayout('2x2')}
              title="2x2"
            >
              <svg width="22" height="22" viewBox="0 0 36 36" fill="none">
                <rect x="4" y="4" width="12" height="12" rx="1.5" fill="currentColor" opacity="0.75"/>
                <rect x="20" y="4" width="12" height="12" rx="1.5" fill="currentColor" opacity="0.75"/>
                <rect x="4" y="20" width="12" height="12" rx="1.5" fill="currentColor" opacity="0.75"/>
                <rect x="20" y="20" width="12" height="12" rx="1.5" fill="currentColor" opacity="0.75"/>
              </svg>
            </button>
            <button
              type="button"
              className="grid-btn grid-clear-grid-btn"
              onClick={() => {
                playerState.slots.slice(0, slotCount).forEach((slot) => {
                  if (slot.camera) playerState.removeCameraFromSlot(slot.id);
                });
                setEvidencePhotos([]);
              }}
              disabled={!playerState.slots.slice(0, slotCount).some((s) => s.camera)}
              title="Limpiar grilla (quitar todas las cámaras)"
              aria-label="Limpiar grilla"
            >
              <Eraser size={20} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </div>
        <div className="recordings-header-right">
          <button
            type="button"
            className="recordings-spoa-header-btn"
            disabled={evidencePhotos.length === 0}
            onClick={openSpoaModal}
            title={
              evidencePhotos.length === 0
                ? 'Captura evidencias con el icono de cámara en cada video'
                : 'Generar o editar documento SPOA / código policía'
            }
          >
            <FileText size={18} strokeWidth={2} aria-hidden />
            <span>Generar SPOA</span>
            {evidencePhotos.length > 0 ? (
              <span className="recordings-spoa-header-badge">{evidencePhotos.length}</span>
            ) : null}
          </button>
        </div>
      </div>

      {/* Video Grid – CameraDropZone por slot (IDLE / PREPARING / READY / ERROR) */}
      <div className="video-grid-container">
        <div className={`video-grid grid-${gridLayout}`}>
          {playerState.slots.slice(0, slotCount).map((slot, index) => {
            const slotPlaybackAnchor =
              slot.playback != null
                ? effectiveRecordingAnchorWallTime(slot.playback, playerState.selectedTime)
                : playerState.selectedTime;
            const slotDownloadStart = playerState.selectedTime;
            const slotDownloadEnd = slot.playback?.endTime;
            const slotCam = slot.camera;
            const slotEvidencePhotos = slotCam
              ? evidencePhotos.filter((p) =>
                  evidencePhotoBelongsToSlotCamera(p, String(slotCam.id), slotCam.name, slot.id)
                )
              : [];
            const slotContent = (
              <CameraDropZone
                slot={slot}
                mode={playerState.mode}
                playbackSpeed={playerState.playbackSpeed}
                slotIndex={index}
                onDrop={(slotId, camera) => addCameraFromSchemaDrop(slotId, camera)}
                onDropSite={handleDropSite}
                onRemove={removeCameraFromSlotAndEvidence}
                onRegisterPlayer={playerState.registerPlayer}
                onUnregisterPlayer={playerState.unregisterPlayer}
                onRetry={(sid) => playerState.prepareStream(sid)}
                onError={playerState.setGlobalError}
                expandedSlotId={expandedSlotId}
                onExpand={handleExpand}
                draggedCamera={draggedCamera}
                getDraggedCameraFromSchema={() => draggedCameraForDropRef.current}
                onEvidenceCapture={handleEvidenceCapture}
                slotEvidencePhotos={slotEvidencePhotos}
                onEvidenceRemove={removeEvidencePhoto}
                onClearPostExpandResume={clearPostExpandResumeForSlot}
                onHlsBufferReset={playerState.bumpHlsStabilityGuard}
                recordingDisplayAnchorWallTime={
                  slot.playback != null ? slotPlaybackAnchor : undefined
                }
                expandedPlayerHost={expandedSlotId === slot.id ? expandedPlayerHost : null}
                networkHealth={networkHealth}
                activeCameraCount={activeCameraCount}
              />
            );
            return (
              <div
                key={slot.id}
                className={`video-slot ${slot.camera ? 'has-camera' : 'empty'} ${draggedCamera ? 'drop-target' : ''}`}
                data-slot-id={slot.id}
                onDragOver={handleSlotDragOver}
                onDrop={(e) => handleSlotDrop(slot.id, e)}
              >
                {slot.camera ? (() => {
                  const siteId = slot.camera.siteId ? Number(slot.camera.siteId) : (slot.camera.site?.id ? Number(slot.camera.site.id) : undefined);
                  const cameraIdNum = slot.camera.id ? Number(slot.camera.id) : undefined;
                  return (
                    <RecordingCard
                      cameraId={slot.camera.id}
                      cameraName={slot.camera.name}
                      siteId={siteId}
                      cameraIdNum={cameraIdNum}
                      startTime={slotDownloadStart}
                      playbackEndTime={slotDownloadEnd}
                      fullDuration="2h"
                      segmentDuration="5 min"
                      fullSizeEstimate="~850 MB"
                      segmentSizeEstimate="~12 MB"
                      isLive={false}
                      canDownload={canRecordingsDownload}
                      playbackRequestId={slot.playback?.requestId}
                      className="recording-card-slot"
                    >
                      {slotContent}
                    </RecordingCard>
                  );
                })() : (
                  slotContent
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/*
        Modal de video ampliado: SIEMPRE montado para que `expandedPlayerHost` (el destino del portal del
        `<video>`) sea un nodo DOM estable. Se oculta con la clase `--hidden`. Sólo así el `CameraVideoPlayer`
        del grid puede teletransportarse aquí sin remontarse (preservando m3u8/ts cache de Hls.js).
        El chrome interno (close, info, playback) se renderiza condicionalmente.
      */}
      {(() => {
        const slot = expandedSlotId != null
          ? playerState.slots.find((s) => s.id === expandedSlotId) ?? null
          : null;
        const isOpen = slot?.camera != null;
        const isNativeFullscreen = fullscreen.isFullscreen;
        return (
          <div
            ref={expandOverlayRef}
            className={`video-expand-overlay${isOpen ? '' : ' video-expand-overlay--hidden'}${isNativeFullscreen ? ' video-expand-overlay--fullscreen' : ''}`}
            onClick={isOpen ? closeExpandedAndExitFullscreen : undefined}
            role="dialog"
            aria-hidden={!isOpen}
            aria-label="Video ampliado"
          >
            <div
              className="video-expand-content"
              onClick={(e) => e.stopPropagation()}
            >
              {isOpen && (
                <button
                  type="button"
                  className="video-expand-close"
                  onClick={closeExpandedAndExitFullscreen}
                  title={isNativeFullscreen ? 'Salir de pantalla completa' : 'Cerrar'}
                >
                  ×
                </button>
              )}
              <div className="video-expand-player" ref={setExpandedPlayerHost}>
                {/*
                  Estados no-READY: el `<CameraVideoPlayer>` del grid no se portala aquí (sólo en READY),
                  así que se renderiza el feedback (spinner / error) en su lugar.
                */}
                {isOpen && slot.streamStatus === 'PREPARING' && (
                  <div className="video-expand-loading" aria-live="polite">
                    <XTAMSpinner variant="inline" message="Preparando stream…" />
                  </div>
                )}
                {isOpen && slot.streamStatus === 'ERROR' && (
                  <div className="video-expand-error">
                    <p className="video-expand-error-text">{slot.error || 'Error de stream'}</p>
                    <button
                      type="button"
                      className="video-expand-retry"
                      onClick={() => playerState.prepareStream(slot.id)}
                    >
                      Reintentar
                    </button>
                  </div>
                )}
              </div>
              {isOpen && (
                <>
                  <div className="video-expand-info">
                    {slot.camera!.site?.name || 'Sin sitio'} — {slot.camera!.name}
                  </div>
                  <div
                    className="playback-section video-expand-playback"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderPlaybackSection()}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Playback Section (oculto en modal ampliado: los mismos controles van dentro del modal) */}
      {expandedSlotId == null && (
        <div className="playback-section">
          {renderPlaybackSection()}
        </div>
      )}

        <RecordingsSpoaModal
          open={spoaModalOpen}
          onClose={dismissSpoaModal}
          onSaved={() => {
            skipNextEvidencePersistRef.current = true;
            setEvidencePhotos([]);
            try {
              sessionStorage.removeItem(`${EVIDENCE_STORAGE_PREFIX}${recordingsContextKey}`);
              const legacyPrefix = `${EVIDENCE_STORAGE_PREFIX}${recordingsContextKey}|`;
              const toRemove: string[] = [];
              for (let i = 0; i < sessionStorage.length; i++) {
                const k = sessionStorage.key(i);
                if (k?.startsWith(legacyPrefix)) toRemove.push(k);
              }
              toRemove.forEach((k) => sessionStorage.removeItem(k));
            } catch { /* modo privado / storage lleno */ }
          }}
          contextKey={recordingsContextKey}
          evidencePhotos={evidencePhotos}
          activeCameras={activeCamerasForSpoa}
        />
      </div>
    </div>
  );
};

export default RecordingsPage;
